/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useRef } from 'react';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';
import {
  Box, Typography, Button, Card, CardContent, Grid, Avatar,
  Stepper, Step, StepLabel, Chip, IconButton, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Switch, FormControlLabel, Divider, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemIcon, ListItemText, Badge,
  ToggleButton, ToggleButtonGroup, Tab, Tabs, Checkbox,
  Drawer, Select, MenuItem, InputLabel, FormControl,
} from '@mui/material';
import {
  TravelExplore, FlightTakeoff, PhotoLibrary, People, Explore,
  ArrowBack, ArrowForward, PlayArrow, Close,
  LocationOn, CalendarToday, Flight, Hotel, DirectionsCar, Attractions,
  CheckCircle, Lock, Public, Share, PersonAdd, Notifications,
  Settings, Shield, Analytics, AttachMoney, Campaign,
  AdminPanelSettings, Support, AutoAwesome, ToggleOn, ToggleOff,
  ConnectWithoutContact, WorkspacePremium, CloudUpload,
  AddCircleOutline, Star, Visibility,
  ConfirmationNumber, Mail, Reply, Schedule, MonitorHeart, Refresh,
  Warning,
} from '@mui/icons-material';

// ─── SEED DATA ───────────────────────────────────────────────────────────────

export const DEMO_TRIPS = [
  {
    id: 'trip-1',
    title: 'East Africa Safari',
    destination: 'Kenya & Tanzania',
    startDate: 'Aug 5, 2026',
    endDate: 'Aug 13, 2026',
    status: 'planning',
    visibility: 'private',
    days: 8,
    items: 6,
    cost: 6800,
    flights: [{ title: 'Kenya Airways KQ003 — JFK → NBO', date: 'Aug 5', status: 'booked', cost: 1480 }],
    hotels: [
      { title: 'Mara Serena Safari Lodge', date: 'Aug 5 - Aug 9', status: 'booked', cost: 2200 },
      { title: 'Serengeti Under Canvas', date: 'Aug 9 - Aug 13', status: 'quoted', cost: 2600 },
    ],
    activities: [
      { title: 'Maasai Mara Game Drive', date: 'Aug 6', cost: 140 },
      { title: 'Hot Air Balloon Safari', date: 'Aug 8', cost: 480 },
      { title: 'Ngorongoro Crater Tour', date: 'Aug 11', cost: 160 },
    ],
    photos: ['Lioness at dawn, Maasai Mara', 'Wildebeest crossing the Mara River', 'Hot air balloon over the Serengeti', 'Elephant herd at a watering hole', 'Acacia silhouette at sunset'],
    companions: [{ name: 'Alex Rivera', status: 'accepted' }, { name: 'Sam Chen', status: 'pending' }],
  },
  {
    id: 'trip-2',
    title: 'Tokyo Adventure',
    destination: 'Tokyo, Japan',
    startDate: 'Sep 3, 2026',
    endDate: 'Sep 12, 2026',
    status: 'planning',
    visibility: 'bffs_travel_partners',
    days: 9,
    items: 8,
    cost: 4500,
    flights: [{ title: 'ANA NH9 — ORD → NRT', date: 'Sep 3', status: 'researching', cost: 1200 }],
    hotels: [{ title: 'Park Hyatt Tokyo', date: 'Sep 3 - Sep 12', status: 'quoted', cost: 2400 }],
    activities: [
      { title: 'TeamLab Planets', date: 'Sep 4', cost: 35 },
      { title: 'Tsukiji Outer Market Food Tour', date: 'Sep 5', cost: 95 },
    ],
    photos: ['Shibuya crossing at night', 'Cherry blossom temple', 'Ramen bowl close-up', 'Tokyo Tower skyline'],
    companions: [],
  },
  {
    id: 'trip-3',
    title: 'Lisbon City Break',
    destination: 'Lisbon, Portugal',
    startDate: 'Oct 2, 2026',
    endDate: 'Oct 8, 2026',
    status: 'upcoming',
    visibility: 'private',
    days: 6,
    items: 4,
    cost: 2100,
    flights: [],
    hotels: [],
    activities: [],
    photos: ['Tram 28 in Alfama', 'Belem Tower at dusk', 'Pasteis de Belem', 'Miradouro sunset view'],
    companions: [{ name: 'Jordan Taylor', status: 'accepted' }],
  },
];

// Find a demo trip by id (used by drill-down navigation).
export const findDemoTrip = (id: string) => DEMO_TRIPS.find(t => t.id === id) || DEMO_TRIPS[0];

// Shared "this responds to clicks" affordance — cursor + teal hover lift/outline.
export const interactiveSx = {
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 4px 16px rgba(45,110,106,0.18)', outline: '1px solid rgba(45,110,106,0.35)' },
  '&:active': { transform: 'translateY(0)', boxShadow: '0 1px 4px rgba(45,110,106,0.2)' },
};

const DEMO_COMMUNITY = [
  { user: 'Alex Rivera', trip: 'Barcelona Street Art Tour', photo: 'Colorful mural on Gothic Quarter wall', likes: 24 },
  { user: 'Sam Chen', trip: 'Kyoto in Autumn', photo: 'Red maple leaves over temple pond', likes: 41 },
  { user: 'Jordan Taylor', trip: 'Cape Town Coast', photo: 'Table Mountain from Blouberg Beach', likes: 18 },
  { user: 'Priya Patel', trip: 'Rajasthan Heritage', photo: 'Amber Fort at golden hour', likes: 33 },
];

const ADMIN_USERS = [
  { name: 'Maya Johnson', email: 'maya.johnson@email.com', tier: 'pro', trips: 3, photos: 12, joined: 'May 2026' },
  { name: 'Alex Rivera', email: 'alex.r@email.com', tier: 'free', trips: 2, photos: 8, joined: 'May 2026' },
  { name: 'Sam Chen', email: 'sam.chen@email.com', tier: 'free', trips: 1, photos: 4, joined: 'Jun 2026' },
  { name: 'Jordan Taylor', email: 'jordan.t@email.com', tier: 'pro', trips: 4, photos: 21, joined: 'Apr 2026' },
  { name: 'Priya Patel', email: 'priya.p@email.com', tier: 'free', trips: 1, photos: 3, joined: 'Jun 2026' },
];

const ADMIN_PLAN_REQUESTS = [
  { id: 1, name: 'Alex Rivera', email: 'alex.r@email.com', current: 'free', requested: 'pro', date: 'Jun 1, 2026', type: 'Upgrade' },
  { id: 2, name: 'Sam Chen', email: 'sam.chen@email.com', current: 'free', requested: 'pro', date: 'Jun 1, 2026', type: 'Upgrade' },
];

const FEATURE_FLAGS = [
  { name: 'master_public', label: 'Public Trips', enabled: true },
  { name: 'master_friends', label: 'Friends System', enabled: true },
  { name: 'booking_enabled', label: 'Booking Engine', enabled: false },
  { name: 'ai_activity_recommendations', label: 'AI Activity Suggestions', enabled: true },
  { name: 'local_events_section', label: 'Local Events', enabled: false },
  { name: 'public_trips', label: 'Public Trip Sharing', enabled: true },
  { name: 'friends_trips', label: 'Friends Trip Sharing', enabled: true },
];

// ─── PHOTO PLACEHOLDER (renders real image when src given, else labeled box) ──

export const PhotoPlaceholder: React.FC<{ label: string; height?: number | string; borderRadius?: number; src?: string }> = ({ label, height = 180, borderRadius = 12, src }) => {
  const [imgError, setImgError] = useState(false);
  if (src && !imgError) {
    return (
      <Box sx={{ height, borderRadius: `${borderRadius}px`, overflow: 'hidden', position: 'relative', bgcolor: '#E8F0EE' }}>
        <img
          src={src}
          alt={label}
          onError={() => setImgError(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </Box>
    );
  }
  return (
    <Box sx={{
      height, borderRadius: `${borderRadius}px`, bgcolor: '#E8F0EE',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      border: '2px dashed #B0CEC8', position: 'relative', overflow: 'hidden',
    }}>
      <Box sx={{ textAlign: 'center', px: 2 }}>
        <PhotoLibrary sx={{ fontSize: 32, color: '#2D6E6A', opacity: 0.4, mb: 0.5 }} />
        <Typography variant="caption" sx={{ color: '#2D6E6A', opacity: 0.7, display: 'block', fontSize: '0.7rem' }}>
          {label}
        </Typography>
      </Box>
    </Box>
  );
};

// ─── NARRATION OVERLAY ──────────────────────────────────────────────────────

export const Narration: React.FC<{ text: string }> = ({ text }) => (
  <Box sx={{
    bgcolor: '#574B90', color: 'white', px: 3, py: 2,
    borderRadius: '16px', mb: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
  }}>
    <Typography variant="body2" sx={{ lineHeight: 1.6, fontFamily: 'Poppins, sans-serif' }}>
      {text}
    </Typography>
  </Box>
);

// ─── USER STEP — WHY PERCORO (value proposition, first screen) ───────────────

export function UserStep_WhyPercoro() {
  const values = [
    {
      icon: <People sx={{ fontSize: 26, color: '#2D6E6A' }} />,
      title: 'Plan together, for real',
      body: 'Invite the friends you are actually traveling with. Everyone sees the itinerary; everyone books their own way.',
    },
    {
      icon: <Lock sx={{ fontSize: 26, color: '#2D6E6A' }} />,
      title: 'Your memories, your visibility',
      body: 'Every photo has its own privacy level — public, friends, BFFs, or private. Change it anytime.',
    },
    {
      icon: <Shield sx={{ fontSize: 26, color: '#2D6E6A' }} />,
      title: 'No public-by-default, no ads',
      body: 'You decide what is shared, and with whom. Your trips are never mined for ads.',
    },
  ];
  return (
    <Box>
      <Box sx={{ background: 'linear-gradient(135deg, #2D6E6A 0%, #1a4a47 50%, #2D6E6A 100%)', borderRadius: 4, p: { xs: 3, sm: 5 }, color: 'white', mb: 3 }}>
        <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: { xs: '1.6rem', sm: '2.2rem' }, mb: 1.5 }}>
          Why Percoro
        </Typography>
        <Typography sx={{ fontSize: { xs: '1.05rem', sm: '1.2rem' }, fontWeight: 600, opacity: 0.95, mb: 2 }}>
          Your trips live in ten different places. Percoro brings them together.
        </Typography>
      </Box>
      <Grid container spacing={2}>
        {values.map((v, i) => (
          <Grid item xs={12} sm={4} key={i}>
            <Card variant="outlined" sx={{ borderRadius: 3, height: '100%', borderColor: '#E0ECEA' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ bgcolor: '#E8F0EE', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                  {v.icon}
                </Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ fontFamily: 'Poppins, sans-serif', mb: 0.5 }}>{v.title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{v.body}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

// ─── USER STEP — SIGNUP ─────────────────────────────────────────────────────

export function UserStep2_Signup() {
  return (
    <Box>
      <Narration text="Getting in takes seconds — email or Google. We keep the front door simple so you get to the part that matters: your trips." />
      <Card sx={{ maxWidth: 400, mx: 'auto', borderRadius: 4, p: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h5" fontWeight={700} color="#2D6E6A" sx={{ fontFamily: 'Poppins, sans-serif' }}>PERCORO</Typography>
          <Typography variant="body2" color="text.secondary">Create your account</Typography>
        </Box>
        <Button fullWidth variant="outlined" sx={{ mb: 2, borderRadius: '30px', textTransform: 'none', py: 1.2, borderColor: '#dadce0' }}>
          Continue with Google
        </Button>
        <Divider sx={{ my: 2 }}>or continue with email</Divider>
        <TextField fullWidth size="small" label="Full Name" value="Maya Johnson" sx={{ mb: 1.5 }} />
        <TextField fullWidth size="small" label="Email" value="maya.johnson@email.com" sx={{ mb: 1.5 }} />
        <TextField fullWidth size="small" label="Password" type="password" value="password" sx={{ mb: 2 }} />
        <Button fullWidth variant="contained" sx={{ bgcolor: '#2D6E6A', borderRadius: '30px', textTransform: 'none', py: 1.2, fontWeight: 700 }}>
          Sign Up
        </Button>
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Chip icon={<CheckCircle sx={{ fontSize: 16 }} />} label="Account created — verified instantly" color="success" size="small" />
        </Box>
      </Card>
    </Box>
  );
}

// ─── USER STEP — HOME ───────────────────────────────────────────────────────

export function UserStep3_Home({ onOpen }: any = {}) {
  return (
    <Box>
      <Narration text="Everything starts here — your trips, your circle, and what needs your attention, in one place. No hunting across apps." />
      {/* Auth Header */}
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0', px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: '12px 12px 0 0' }}>
        <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, color: '#2D6E6A', fontSize: '1.1rem' }}>
          PERCORO
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CloudUpload sx={{ color: '#666', fontSize: 20 }} />
          <Avatar sx={{ width: 32, height: 32, bgcolor: '#2D6E6A', fontSize: '0.8rem' }}>M</Avatar>
          <Settings sx={{ color: '#666', fontSize: 20 }} />
        </Box>
      </Box>
      {/* Tabs */}
      <Box sx={{ bgcolor: 'white', borderBottom: '2px solid #2D6E6A', display: 'flex', justifyContent: 'center', gap: 4, py: 1 }}>
        {['My Trips', 'My Photos', 'Community'].map((tab, i) => (
          <Box key={tab} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: i === 0 ? '#2D6E6A' : '#999', borderBottom: i === 0 ? '3px solid #2D6E6A' : 'none', pb: 0.5, cursor: 'pointer' }}>
            {i === 0 && <TravelExplore sx={{ fontSize: 18 }} />}
            {i === 1 && <PhotoLibrary sx={{ fontSize: 18 }} />}
            {i === 2 && <Explore sx={{ fontSize: 18 }} />}
            <Typography variant="body2" fontWeight={i === 0 ? 600 : 400}>{tab}</Typography>
          </Box>
        ))}
      </Box>
      {/* Welcome header */}
      <Box sx={{ background: 'linear-gradient(135deg, #2D6E6A 0%, #007a6e 100%)', px: 3, py: 3, color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ fontFamily: 'Poppins, sans-serif', mb: 0.5 }}>Welcome back, Maya.</Typography>
            <Typography sx={{ opacity: 0.9, fontSize: '0.9rem' }}>Here's what's happening with your trips and friends.</Typography>
          </Box>
          <Badge badgeContent={4} color="error">
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '50%', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid rgba(255,255,255,0.5)' }}>
              <Notifications sx={{ color: 'white', fontSize: 22 }} />
            </Box>
          </Badge>
        </Box>
      </Box>
      {/* Dashboard cards */}
      <Box sx={{ px: 2, py: 2, bgcolor: '#F8FAFB' }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, fontFamily: 'Poppins, sans-serif' }}>Your dashboard</Typography>
        <Grid container spacing={1.5} sx={{ mb: 3 }}>
          {[
            { count: 1, label: 'Friend invites', color: '#e53935', icon: <PersonAdd sx={{ color: '#e53935', fontSize: 16 }} /> },
            { count: 0, label: 'Announcements', color: '#2D6E6A', icon: <Notifications sx={{ color: '#2D6E6A', fontSize: 16 }} /> },
            { count: 3, label: 'Trip reminders', color: '#f57c00', icon: <Flight sx={{ color: '#f57c00', fontSize: 16 }} /> },
            { count: 0, label: 'Friend updates', color: '#8e24aa', icon: <People sx={{ color: '#8e24aa', fontSize: 16 }} /> },
          ].map((n, i) => (
            <Grid item xs={3} key={i}>
              <Card sx={{ borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: n.count > 0 ? '1px solid #f0e0d0' : '1px solid #f0f0f0' }}>
                <CardContent sx={{ textAlign: 'center', py: 1.5, px: 1, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ bgcolor: 'white', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 0.5, border: '1px solid #e0e0e0' }}>
                    {n.icon}
                  </Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: n.count > 0 ? n.color : '#bdbdbd', fontSize: '1.1rem' }}>{n.count}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', lineHeight: 1.2, display: 'block' }}>{n.label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        {/* Quick actions */}
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, fontFamily: 'Poppins, sans-serif' }}>Quick actions</Typography>
        <Grid container spacing={1.5}>
          {[
            { title: 'My Trips', sub: 'View and manage', icon: <TravelExplore sx={{ fontSize: 24 }} />, iconColor: '#2D6E6A', bgColor: '#C8DDD8' },
            { title: 'Import a Trip', sub: 'Upload booking PDF', icon: <FlightTakeoff sx={{ fontSize: 24 }} />, iconColor: '#e53935', bgColor: '#F0E0D0' },
            { title: 'Companions', sub: 'Invite companions', icon: <ConnectWithoutContact sx={{ fontSize: 24 }} />, iconColor: '#3949ab', bgColor: '#D0D8E8' },
            { title: 'Friends', sub: 'Connect and share', icon: <People sx={{ fontSize: 24 }} />, iconColor: '#8e24aa', bgColor: '#D8D0E8' },
            { title: 'My Photos', sub: 'Travel gallery', icon: <PhotoLibrary sx={{ fontSize: 24 }} />, iconColor: '#f57c00', bgColor: '#EDE0C8' },
            { title: 'Community', sub: 'Explore trips', icon: <Explore sx={{ fontSize: 24 }} />, iconColor: '#0288d1', bgColor: '#C8DCE8' },
          ].map((card, i) => (
            <Grid item xs={4} key={i}>
              <Card
                onClick={() => {
                  if (!onOpen) return;
                  if (card.title === 'My Trips' || card.title === 'Import a Trip') onOpen({ screen: 'trip', data: DEMO_TRIPS[0] });
                  else if (card.title === 'Community') onOpen({ screen: 'community' });
                  else if (card.title === 'My Photos') onOpen({ screen: 'photos', data: DEMO_TRIPS[0] });
                }}
                sx={{ borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', cursor: 'pointer', '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.12)', transform: 'translateY(-2px)', outline: '1px solid rgba(45,110,106,0.35)' }, transition: 'all 0.2s' }}>
                <CardContent sx={{ textAlign: 'center', py: 2, px: 1, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ bgcolor: 'white', borderRadius: '50%', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1, color: card.iconColor, border: '2px solid ' + card.iconColor, boxShadow: '0 0 0 4px ' + card.bgColor }}>
                    {card.icon}
                  </Box>
                  <Typography variant="caption" fontWeight={600} sx={{ display: 'block' }}>{card.title}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>{card.sub}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        {/* Plan banner */}
        <Card sx={{ borderRadius: '16px', mt: 2, border: '1px solid #2D6E6A', boxShadow: 'none' }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
            <Box sx={{ bgcolor: 'white', borderRadius: '50%', width: 40, height: 40, minWidth: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #2D6E6A' }}>
              <WorkspacePremium sx={{ color: '#2D6E6A', fontSize: 20 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={600}>You're on the Pro Plan</Typography>
              <Typography variant="caption" color="text.secondary">15 GB storage, trip companions, expiration control, and more.</Typography>
            </Box>
            <Button size="small" variant="outlined" sx={{ borderColor: '#2D6E6A', color: '#2D6E6A', borderRadius: '20px', textTransform: 'none', fontWeight: 600 }}>View Plans</Button>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

// ─── USER STEP — CREATE TRIP ────────────────────────────────────────────────

export function UserStep4_CreateTrip() {
  const [created, setCreated] = useState(false);
  return (
    <Box>
      <Narration text="Upload your existing bookings and package your trip in seconds. One complete itinerary — flights, hotels, and activities — without manual entry." />
      {!created ? (
        <Card sx={{ borderRadius: 3, maxWidth: 500, mx: 'auto' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2, fontFamily: 'Poppins, sans-serif' }}>Create a New Trip</Typography>
            <TextField fullWidth size="small" label="Trip Title" value="East Africa Safari" sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <TextField fullWidth size="small" label="Destination" value="Kenya & Tanzania" sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Start Date" type="date" value="2026-08-05" InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="End Date" type="date" value="2026-08-13" InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Grid>
            </Grid>
            <TextField fullWidth size="small" label="Description (optional)" value="Game drives across the Maasai Mara and Serengeti, with a balloon safari and Ngorongoro crater day." multiline rows={2} sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <Button fullWidth variant="contained" onClick={() => setCreated(true)}
              sx={{ bgcolor: '#2D6E6A', borderRadius: 2, textTransform: 'none', fontWeight: 700, py: 1.2, '&:hover': { bgcolor: '#1F4E4A' } }}>
              Create Trip
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: '#E8F5E9', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
            <CheckCircle sx={{ color: '#4CAF50', fontSize: 32 }} />
          </Box>
          <Typography variant="h6" fontWeight={700}>Trip Created!</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>East Africa Safari — Aug 5-13, 2026</Typography>
          <Typography variant="caption" color="text.secondary">Click Next to see the trip detail page →</Typography>
        </Box>
      )}
    </Box>
  );
}

// ─── USER STEP — TRIP DETAIL ────────────────────────────────────────────────

export function UserStep5_TripDetail({ trip: tripProp, onOpen }: any = {}) {
  const trip = tripProp || DEMO_TRIPS[0];
  const [planningOpen, setPlanningOpen] = useState(true);
  return (
    <Box>
      <Narration text="The whole trip in one view — flights, hotels, activities, costs. Share it with the people coming along, or print it for the road." />
      {/* AppBar */}
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0', px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: '12px 12px 0 0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ArrowBack sx={{ color: '#666', fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight={700}>Trip Details</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label="Planning" size="small" color="info" sx={{ fontWeight: 600 }} />
          <Typography sx={{ color: '#666', cursor: 'pointer' }}>⋮</Typography>
        </Box>
      </Box>

      {/* Trip Header Card */}
      <Card sx={{ borderRadius: 0, borderBottom: '1px solid #e0e0e0', boxShadow: 'none' }}>
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h5" fontWeight={700}>{trip.title}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <LocationOn sx={{ color: '#FF6B6B', fontSize: 18 }} />
                <Typography variant="body1" fontWeight={500}>{trip.destination}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, color: 'text.secondary' }}>
                <CalendarToday sx={{ fontSize: 16 }} />
                <Typography variant="body2">{trip.startDate} - {trip.endDate}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Chip icon={<Lock />} label="Private" size="small" variant="outlined" />
                {trip.companions.length > 0 && (
                  <Chip icon={<People />} label={`Shared with ${trip.companions.length}`} size="small"
                    sx={{ bgcolor: 'rgba(0,180,160,0.08)', color: '#2D6E6A' }} />
                )}
              </Box>
            </Box>
            <Button variant="contained" startIcon={<Share />} size="small"
              sx={{ bgcolor: '#2D6E6A', borderRadius: 2, textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}>
              Share Trip
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Trip Planning Panel */}
      <Paper sx={{ borderRadius: 0, overflow: 'hidden', boxShadow: 'none', border: '1px solid #e0e0e0', borderTop: 0 }}>
        <Box onClick={() => setPlanningOpen(!planningOpen)}
          sx={{ bgcolor: '#2D6E6A', color: 'white', px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}>
          <Attractions sx={{ fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight={600}>Trip Planning</Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, ml: 1 }}>
            ({trip.flights.length + trip.hotels.length + trip.activities.length} items)
          </Typography>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button size="small" variant="outlined" startIcon={<CloudUpload sx={{ fontSize: 16 }} />}
              sx={{ borderColor: 'rgba(255,255,255,0.6)', color: 'white', textTransform: 'none', fontWeight: 600, borderRadius: 2, fontSize: '0.75rem' }}>
              Upload
            </Button>
            <Typography>{planningOpen ? '▲' : '▼'}</Typography>
          </Box>
        </Box>
        {planningOpen && (
          <Box sx={{ p: 2 }}>
            {/* Tabs */}
            <Box sx={{ display: 'flex', gap: 2, borderBottom: '1px solid #e0e0e0', mb: 2, pb: 1 }}>
              {[
                { icon: <Flight sx={{ fontSize: 18 }} />, label: 'Flights', count: trip.flights.length },
                { icon: <Hotel sx={{ fontSize: 18 }} />, label: 'Hotels', count: trip.hotels.length },
                { icon: <Attractions sx={{ fontSize: 18 }} />, label: 'Activities', count: trip.activities.length },
              ].map((tab, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: i === 0 ? '#2D6E6A' : '#999', cursor: 'pointer', pb: 0.5, borderBottom: i === 0 ? '2px solid #2D6E6A' : 'none' }}>
                  {tab.icon}
                  <Typography variant="caption" fontWeight={i === 0 ? 600 : 400}>{tab.label}</Typography>
                  {tab.count > 0 && <Badge badgeContent={tab.count} color="secondary" sx={{ ml: 0.5, '& .MuiBadge-badge': { fontSize: '0.6rem', height: 16, minWidth: 16 } }} />}
                </Box>
              ))}
            </Box>
            {/* Items */}
            {trip.flights.map((f: any, i: number) => (
              <Card key={'f' + i} variant="outlined" sx={{ borderRadius: 2, mb: 1.5 }}>
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Flight sx={{ color: '#2196F3', fontSize: 20 }} />
                    <Box sx={{ flex: 1 }}><Typography variant="subtitle2" fontWeight={600}>{f.title}</Typography><Typography variant="caption" color="text.secondary">{f.date}</Typography></Box>
                    <Chip label={f.status} size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                    <Typography variant="caption" fontWeight={600} sx={{ color: '#2D6E6A' }}>{'$' + f.cost}</Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
            {trip.hotels.map((h: any, i: number) => (
              <Card key={'h' + i} variant="outlined" sx={{ borderRadius: 2, mb: 1.5 }}>
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Hotel sx={{ color: '#4A6FA5', fontSize: 20 }} />
                    <Box sx={{ flex: 1 }}><Typography variant="subtitle2" fontWeight={600}>{h.title}</Typography><Typography variant="caption" color="text.secondary">{h.date}</Typography></Box>
                    <Chip label={h.status} size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                    <Typography variant="caption" fontWeight={600} sx={{ color: '#2D6E6A' }}>{'$' + h.cost}</Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
            {trip.activities.map((a: any, i: number) => (
              <Card key={'a' + i} variant="outlined" sx={{ borderRadius: 2, mb: 1.5 }}>
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Attractions sx={{ color: '#2D6E6A', fontSize: 20 }} />
                    <Box sx={{ flex: 1 }}><Typography variant="subtitle2" fontWeight={600}>{a.title}</Typography><Typography variant="caption" color="text.secondary">{a.date}</Typography></Box>
                    <Typography variant="caption" fontWeight={600} sx={{ color: '#2D6E6A' }}>{'$' + a.cost}</Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Paper>

      {/* Trip Overview stats */}
      <Card sx={{ borderRadius: 0, bgcolor: 'rgba(0,180,160,0.05)', boxShadow: 'none', border: '1px solid #e0e0e0', borderTop: 0 }}>
        <CardContent sx={{ py: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={4} sx={{ textAlign: 'center' }}>
              <Typography variant="h6" fontWeight={700} color="#2D6E6A">{trip.days}</Typography>
              <Typography variant="caption" color="text.secondary">Days</Typography>
            </Grid>
            <Grid item xs={4} sx={{ textAlign: 'center' }}>
              <Typography variant="h6" fontWeight={700} color="#2D6E6A">{trip.items}</Typography>
              <Typography variant="caption" color="text.secondary">Items</Typography>
            </Grid>
            <Grid item xs={4} sx={{ textAlign: 'center' }}>
              <Typography variant="h6" fontWeight={700} color="#2D6E6A">{'$' + trip.cost.toLocaleString()}</Typography>
              <Typography variant="caption" color="text.secondary">Est. Total</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Trip Intelligence */}
      <Card sx={{ borderRadius: '0 0 12px 12px', boxShadow: 'none', border: '1px solid #e0e0e0', borderTop: 0 }}>
        <CardContent sx={{ py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">Trip Intelligence (Weather, Visa, Packing)</Typography>
          <Typography color="text.secondary">▼</Typography>
        </CardContent>
      </Card>
    </Box>
  );
}

// ─── USER STEP — PHOTOS ─────────────────────────────────────────────────────

export function UserStep6_Photos() {
  const trip = DEMO_TRIPS[0];
  const [tabValue, setTabValue] = useState(0);
  const visData = [
    { label: 'Lioness at dawn, Maasai Mara', src: '/demo/ea-1.jpg', vis: 'public', visLabel: 'Public', visColor: { bg: '#E8F5E9', color: '#4CAF50' } },
    { label: 'Wildebeest crossing the Mara River', src: '/demo/ea-2.jpg', vis: 'public', visLabel: 'Public', visColor: { bg: '#E8F5E9', color: '#4CAF50' } },
    { label: 'Hot air balloon over the Serengeti', src: '/demo/ea-3.jpg', vis: 'bffs', visLabel: 'BFFs', visColor: { bg: '#FFF3E0', color: '#E65100' } },
    { label: 'Elephant herd at a watering hole', src: '/demo/ea-4.jpg', vis: 'friends', visLabel: 'Friends', visColor: { bg: '#E8F5F3', color: '#2D6E6A' } },
    { label: 'Acacia silhouette at sunset', src: '/demo/ea-5.jpg', vis: 'private', visLabel: 'Private', visColor: { bg: '#FFEBEE', color: '#f44336' } },
  ];
  return (
    <Box>
      <Narration text="Add photos before, during, and after the trip and share them with friends — each one with its own privacy level, public to private. AI tags and captions them as you upload." />
      {/* Blue gradient header */}
      <Box sx={{ background: 'linear-gradient(135deg, #4A6FA5 0%, #3D5A8A 100%)', borderRadius: '12px 12px 0 0', color: 'white', pt: 2, pb: 2, px: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ArrowBack sx={{ color: 'white', fontSize: 20, mr: 1 }} />
            <Box>
              <Typography variant="h6" fontWeight={600}>Trip Photos</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>5 photos · 2 public · 1 BFFs · 1 friends · 1 private</Typography>
            </Box>
          </Box>
          <Button variant="contained" size="small" startIcon={<CloudUpload />}
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', borderRadius: '20px', textTransform: 'none', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>
            Upload
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {['All (5)', 'Public (2)', 'Friends (2)', 'Private (1)'].map((label, i) => (
            <Typography key={i} variant="body2" onClick={() => setTabValue(i)}
              sx={{ color: tabValue === i ? 'white' : 'rgba(255,255,255,0.6)', cursor: 'pointer', borderBottom: tabValue === i ? '2px solid white' : 'none', pb: 0.5, fontWeight: tabValue === i ? 600 : 400 }}>
              {label}
            </Typography>
          ))}
        </Box>
      </Box>
      {/* Photo grid with visibility chips */}
      <Box sx={{ p: 2, bgcolor: '#F8FAFB', borderRadius: '0 0 12px 12px' }}>
        <Grid container spacing={1.5}>
          {visData.map((photo, i) => (
            <Grid item xs={6} sm={4} key={i}>
              <Box sx={{ position: 'relative', cursor: 'pointer' }}>
                <PhotoPlaceholder label={photo.label} src={photo.src} height={140} borderRadius={12} />
                <Box sx={{ position: 'absolute', top: 6, right: 6, bgcolor: 'rgba(0,0,0,0.5)', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ color: 'white', fontSize: 12 }}>⋮</Typography>
                </Box>
                <Chip
                  icon={photo.vis === 'public' ? <Public sx={{ fontSize: 10 }} /> : photo.vis === 'private' ? <Lock sx={{ fontSize: 10 }} /> : <People sx={{ fontSize: 10 }} />}
                  label={photo.visLabel} size="small"
                  sx={{ position: 'absolute', bottom: 6, left: 6, bgcolor: photo.visColor.bg, color: photo.visColor.color, fontWeight: 500, fontSize: '0.65rem', height: 22, '& .MuiChip-icon': { color: photo.visColor.color } }} />
              </Box>
            </Grid>
          ))}
          <Grid item xs={6} sm={4}>
            <Box sx={{ height: 140, borderRadius: '12px', border: '2px dashed #B0CEC8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', cursor: 'pointer', bgcolor: '#F8FAFB' }}>
              <CloudUpload sx={{ color: '#2D6E6A', opacity: 0.5, fontSize: 28 }} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>Add Photos</Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

// ─── USER STEP — MY TRIPS ───────────────────────────────────────────────────

export function UserStep_MyTrips() {
  const [filter, setFilter] = useState('all');
  const myTrips = DEMO_TRIPS.slice(1); // East Africa Safari created in a later step
  const filtered = filter === 'all' ? myTrips : myTrips.filter(t => t.status === filter);
  return (
    <Box>
      <Narration text="Every trip you've planned or taken, in one library. Find any trip by name, place, or confirmation code — instead of digging through your inbox." />
      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <ArrowBack sx={{ color: '#666', fontSize: 20 }} />
        <Typography variant="h5" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, color: '#2D6E6A', flexGrow: 1 }}>
          My Trips
        </Typography>
        <Button variant="contained" size="small" startIcon={<AddCircleOutline />}
          sx={{ bgcolor: '#2D6E6A', borderRadius: '24px', textTransform: 'none', fontWeight: 600, px: 2, '&:hover': { bgcolor: '#1F4E4A' } }}>
          Create Trip
        </Button>
        <Button variant="outlined" size="small"
          sx={{ borderColor: '#e0e0e0', color: '#555', borderRadius: '24px', textTransform: 'none', fontWeight: 600, px: 2 }}>
          Filter / Export
        </Button>
      </Box>
      {/* Search bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, mb: 2, borderRadius: '28px', bgcolor: '#f5f5f5', border: '1px solid #eee' }}>
        <Typography sx={{ color: '#bbb', fontSize: '0.88rem' }}>Search by trip name, destination, or reservation code...</Typography>
      </Box>
      {/* Trip cards - 2 column grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
        {filtered.map((trip, i) => (
          <Box key={i} sx={{
            borderRadius: '16px', bgcolor: '#fff', borderLeft: '5px solid #2D6E6A',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden', cursor: 'pointer',
            '&:hover': { boxShadow: '0 6px 20px rgba(45,110,106,0.12)', transform: 'translateY(-2px)' },
            transition: 'all 0.2s',
          }}>
            {/* Card top */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 1.5, pb: 0.5 }}>
              <Checkbox size="small" sx={{ p: 0, color: '#ddd' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label={trip.status === 'planning' ? 'Planning' : 'Upcoming'} size="small"
                  color={trip.status === 'planning' ? 'info' : 'warning'} sx={{ height: 22, fontSize: '0.68rem', fontWeight: 600 }} />
                <Typography sx={{ color: '#ccc', cursor: 'pointer' }}>⋮</Typography>
              </Box>
            </Box>
            {/* Card body */}
            <Box sx={{ px: 2, pb: 1.5 }}>
              <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '1rem', color: '#1a1a1a', mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {trip.title}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <LocationOn sx={{ fontSize: 15, color: '#aaa' }} />
                <Typography sx={{ fontSize: '0.82rem', color: '#666', fontWeight: 500 }}>{trip.destination}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, color: '#888' }}>
                <CalendarToday sx={{ fontSize: 13 }} />
                <Typography sx={{ fontSize: '0.78rem' }}>{trip.startDate} - {trip.endDate}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35, px: 0.75, py: 0.2, borderRadius: '6px', bgcolor: 'rgba(45,110,106,0.1)' }}>
                  <Typography sx={{ fontSize: '0.64rem', fontWeight: 600, color: '#2D6E6A' }}>Individual</Typography>
                </Box>
                <Box sx={{ flex: 1 }} />
                <Lock sx={{ fontSize: 13, color: '#ccc' }} />
                {trip.companions.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, color: '#2D6E6A' }}>
                    <People sx={{ fontSize: 13 }} />
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 600 }}>{trip.companions.length}</Typography>
                  </Box>
                )}
              </Box>
            </Box>
            {/* Upload CTA */}
            <Box sx={{ px: 2, pb: 1, pt: 0.5, borderTop: '1px solid #f5f5f5' }}>
              <Button fullWidth variant="outlined" size="small" startIcon={<CloudUpload sx={{ fontSize: 16 }} />}
                sx={{ borderColor: '#2D6E6A', color: '#2D6E6A', borderRadius: '10px', textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', py: 0.7 }}>
                Upload Booking
              </Button>
            </Box>
          </Box>
        ))}
      </Box>
      <Typography variant="caption" sx={{ color: '#bbb', mt: 2, display: 'block' }}>{filtered.length} trips</Typography>
    </Box>
  );
}

// ─── USER STEP — INVITE FRIENDS TO TRIP ─────────────────────────────────────

export function UserStep_InviteFriends() {
  const trip = DEMO_TRIPS[0];
  return (
    <Box>
      <Narration text="Invite friends interested in scheduling similar trips to share itinerary details. For travel partners, organize trips with your circle where everyone knows the destination and makes their own travel arrangements — how trips are really planned between friends." />
      <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Share sx={{ color: '#2D6E6A' }} />
          <Typography variant="h6" fontWeight={700}>Share Trip</Typography>
        </Box>
        <Box sx={{ p: 2.5 }}>
          {/* Share link */}
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>Share Link</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Box sx={{ flex: 1, px: 1.5, py: 1, bgcolor: '#f5f5f5', borderRadius: 1, fontSize: '0.8rem', color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              percoro.com/trips/abc-123-def
            </Box>
            <Button variant="outlined" size="small" sx={{ borderColor: '#2D6E6A', color: '#2D6E6A', borderRadius: 2, textTransform: 'none', minWidth: 80 }}>Copy</Button>
          </Box>
          <Divider sx={{ my: 1.5 }} />
          {/* Personal note */}
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>Add a personal note</Typography>
          <Box sx={{ p: 1.5, bgcolor: '#f8f8f8', borderRadius: 2, mb: 2, fontSize: '0.85rem', color: '#666', border: '1px solid #e0e0e0' }}>
            Hey! I'm planning my {trip.title} and wanted to share it with you — take a look! 🌍
          </Box>
          <Divider sx={{ my: 1.5 }} />
          {/* Companion toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box>
              <Typography variant="subtitle2" fontWeight={600}>Invite as Travel Companion</Typography>
              <Typography variant="caption" color="text.secondary">Companion can add photos and contribute to the trip</Typography>
            </Box>
            <Checkbox defaultChecked sx={{ color: '#2D6E6A', '&.Mui-checked': { color: '#2D6E6A' } }} />
          </Box>
          <Box sx={{ px: 1.5, py: 1, bgcolor: '#f8f8f8', borderRadius: 2, mb: 2 }}>
            <Typography variant="caption" color="text.secondary">Access expires (optional)</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              {[1, 3, 7].map(d => (
                <Chip key={d} label={d + ' day' + (d > 1 ? 's' : '') + ' before'} size="small" variant={d === 3 ? 'filled' : 'outlined'}
                  sx={d === 3 ? { bgcolor: '#2D6E6A', color: 'white' } : {}} />
              ))}
            </Box>
          </Box>
          <Divider sx={{ my: 1.5 }} />
          {/* Friends list */}
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Share with friends</Typography>
          {['Alex Rivera', 'Sam Chen', 'Priya Patel'].map((name, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', py: 0.75, borderRadius: 1 }}>
              <Checkbox size="small" defaultChecked={i < 2} sx={{ color: '#2D6E6A', '&.Mui-checked': { color: '#2D6E6A' } }} />
              <Avatar sx={{ width: 32, height: 32, bgcolor: ['#2D6E6A', '#3949ab', '#8e24aa'][i], fontSize: '0.8rem', mr: 1 }}>{name[0]}</Avatar>
              <Box>
                <Typography variant="body2" fontWeight={500}>{name}</Typography>
                <Typography variant="caption" color="text.secondary">{name.toLowerCase().replace(' ', '.') + '@email.com'}</Typography>
              </Box>
            </Box>
          ))}
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>Invite someone new to Percoro</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>They'll get a friend invite + trip share.</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flex: 1, px: 1.5, py: 0.75, bgcolor: '#f5f5f5', borderRadius: 1, fontSize: '0.8rem', color: '#bbb' }}>Their name</Box>
            <Box sx={{ flex: 1.5, px: 1.5, py: 0.75, bgcolor: '#f5f5f5', borderRadius: 1, fontSize: '0.8rem', color: '#bbb' }}>Email address</Box>
            <Button size="small" variant="outlined" sx={{ borderColor: '#2D6E6A', color: '#2D6E6A', textTransform: 'none' }}>Add</Button>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2.5 }}>
            <Button sx={{ textTransform: 'none', color: '#999' }}>Cancel</Button>
            <Button variant="contained" startIcon={<Share />}
              sx={{ bgcolor: '#2D6E6A', borderRadius: 2, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#1F4E4A' } }}>
              Send to 2 Friends
            </Button>
          </Box>
        </Box>
      </Card>
    </Box>
  );
}

// ─── USER STEP — COMMUNITY (Friends feed, full-screen vertical) ──────────────

export function UserStep_CommunityFriends() {
  return (
    <Box>
      <Narration text="Share your photos in a secure social space with your friends. Update visibility from friends to private or public at any time. Let them book a similar trip by tapping the itinerary." />
      {/* Simulated full-screen card */}
      <Box sx={{ borderRadius: 3, overflow: 'hidden', position: 'relative', height: 'min(78vh, 760px)', minHeight: 520, bgcolor: '#000' }}>
        {/* Blurred background */}
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #6b8e6b 0%, #3d5a3d 100%)', opacity: 0.7 }} />
        {/* Photo */}
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <PhotoPlaceholder label="Elephant herd at a watering hole" src="/demo/ea-4.jpg" height="100%" borderRadius={0} />
        </Box>
        {/* Gradient overlay */}
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, transparent 30%, transparent 50%, rgba(0,0,0,0.7) 100%)' }} />
        {/* Tab pills */}
        <Box sx={{ position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', display: 'flex', bgcolor: 'rgba(0,0,0,0.5)', borderRadius: '30px', p: 0.5 }}>
          <Box sx={{ px: 2.5, py: 0.5, borderRadius: '20px', color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Public sx={{ fontSize: 16 }} /> Discover
          </Box>
          <Box sx={{ px: 2.5, py: 0.5, borderRadius: '20px', bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <People sx={{ fontSize: 16 }} /> Friends
          </Box>
        </Box>
        {/* Close + info buttons */}
        <Box sx={{ position: 'absolute', top: 12, right: 12, display: 'flex', gap: 1 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Visibility sx={{ color: 'white', fontSize: 18 }} />
          </Box>
          <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Close sx={{ color: 'white', fontSize: 18 }} />
          </Box>
        </Box>
        {/* Progress dots (left) */}
        <Box sx={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {[0,1,2,3].map(i => (
            <Box key={i} sx={{ width: 4, height: i === 0 ? 24 : 12, borderRadius: 2, bgcolor: i === 0 ? '#2D6E6A' : 'rgba(255,255,255,0.3)' }} />
          ))}
        </Box>
        {/* Author + content overlay */}
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Avatar sx={{ width: 36, height: 36, border: '2px solid white', bgcolor: '#8e24aa', fontSize: '0.85rem' }}>J</Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 600, textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>Jordan Taylor</Typography>
              <Chip icon={<People sx={{ fontSize: 12, color: '#2D6E6A !important' }} />} label="Friend" size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.9)', color: '#2D6E6A', fontWeight: 600, fontSize: '0.65rem', height: 20 }} />
            </Box>
          </Box>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, mb: 0.5, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>Serengeti Crossing</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <LocationOn sx={{ color: 'white', fontSize: 18 }} />
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 500, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>Serengeti, Tanzania</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
            {['safari', 'serengeti', 'wildlife', 'tanzania'].map(tag => (
              <Box key={tag} sx={{ px: 1, py: 0.25, bgcolor: 'rgba(0,180,160,0.85)', borderRadius: 1, fontSize: '0.65rem', color: 'white', fontWeight: 500 }}>{tag}</Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ color: '#FF6B6B', mr: 0.5, fontSize: 20 }}>♥</Box>
              <Typography variant="body2" sx={{ color: 'white' }}>57</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ color: '#FFD700', mr: 0.5, fontSize: 20 }}>🔖</Box>
              <Typography variant="body2" sx={{ color: 'white' }}>19</Typography>
            </Box>
            <Button variant="contained" size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', borderRadius: '20px', textTransform: 'none', px: 2, ml: 'auto' }}>
              See Itinerary
            </Button>
          </Box>
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, textAlign: 'center' }}>
        Swipe up for more · Double-tap to zoom
      </Typography>
    </Box>
  );
}

// ─── ADMIN STEP 1 — FEATURES ────────────────────────────────────────────────

function AdminStep1_Features() {
  const [flags, setFlags] = useState(FEATURE_FLAGS);
  return (
    <Box>
      <Narration text="Feature flags — toggle any feature on or off instantly. No deployments. Full admin control over the product surface." />
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Feature Flags</Typography>
      {flags.map((flag, i) => (
        <Card key={i} variant="outlined" sx={{ borderRadius: 2, mb: 1, px: 2, py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="subtitle2" fontWeight={600}>{flag.label}</Typography>
              <Typography variant="caption" color="text.secondary">{flag.name}</Typography>
            </Box>
            <Switch checked={flag.enabled} color="primary"
              onChange={() => setFlags(prev => prev.map((f, j) => j === i ? { ...f, enabled: !f.enabled } : f))} />
          </Box>
        </Card>
      ))}
    </Box>
  );
}

// ─── ADMIN STEP 2 — NOTIFICATIONS ──────────────────────────────────────────

function AdminStep2_Notifications() {
  return (
    <Box>
      <Narration text="Admin notification broadcasts — send announcements to all users or targeted segments. Full delivery tracking." />
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Notification Broadcasts</Typography>
      {[
        { title: 'Welcome to Percoro!', type: 'All Users', sent: '892', date: 'May 28, 2026', status: 'delivered' },
        { title: 'Pro plan — free for early access', type: 'Free Users', sent: '654', date: 'Jun 1, 2026', status: 'delivered' },
        { title: 'New: Trip Companions feature', type: 'Pro Users', sent: '238', date: 'Jun 1, 2026', status: 'sending' },
      ].map((n, i) => (
        <Card key={i} variant="outlined" sx={{ borderRadius: 2, mb: 1.5, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="subtitle2" fontWeight={600}>{n.title}</Typography>
              <Typography variant="caption" color="text.secondary">{n.type} · {n.date}</Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Chip label={n.status} size="small" color={n.status === 'delivered' ? 'success' : 'info'} />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{n.sent} sent</Typography>
            </Box>
          </Box>
        </Card>
      ))}
    </Box>
  );
}

// ─── ADMIN STEP 3 — PLANS ───────────────────────────────────────────────────

function AdminStep3_Plans() {
  const [requests, setRequests] = useState(ADMIN_PLAN_REQUESTS);
  return (
    <Box>
      <Narration text="Plan management — review and approve upgrade requests. One click to change a user's tier. Email confirmation fires automatically." />
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Plan Requests</Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#F5F5F5' }}>
              <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Current</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Requested</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map((r) => (
              <TableRow key={r.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: '#2D6E6A', fontSize: '0.75rem' }}>{r.name[0]}</Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{r.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{r.email}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell><Chip label={r.current} size="small" variant="outlined" /></TableCell>
                <TableCell><Chip label={r.requested} size="small" sx={{ bgcolor: '#2D6E6A', color: 'white', fontWeight: 600 }} /></TableCell>
                <TableCell><Typography variant="caption">{r.date}</Typography></TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                    <Button size="small" variant="contained"
                      onClick={() => setRequests(prev => prev.filter(p => p.id !== r.id))}
                      sx={{ bgcolor: '#2D6E6A', textTransform: 'none', borderRadius: 2, fontSize: '0.75rem' }}>Approve</Button>
                    <Button size="small" variant="outlined" color="error"
                      onClick={() => setRequests(prev => prev.filter(p => p.id !== r.id))}
                      sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.75rem' }}>Reject</Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ─── ADMIN STEP 4 — USERS ───────────────────────────────────────────────────

function AdminStep4_Users() {
  return (
    <Box>
      <Narration text="User management — privacy-first search, role management, audit logging. Every admin action is tracked and logged." />
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Users</Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#F5F5F5' }}>
              <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Plan</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Trips</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Photos</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Joined</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ADMIN_USERS.map((u, i) => (
              <TableRow key={i} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: '#2D6E6A', fontSize: '0.75rem' }}>{u.name[0]}</Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{u.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={u.tier} size="small"
                    sx={{ bgcolor: u.tier === 'pro' ? '#2D6E6A' : '#F5F5F5', color: u.tier === 'pro' ? 'white' : 'text.primary', fontWeight: 600 }} />
                </TableCell>
                <TableCell>{u.trips}</TableCell>
                <TableCell>{u.photos}</TableCell>
                <TableCell><Typography variant="caption">{u.joined}</Typography></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ─── OUTRO ──────────────────────────────────────────────────────────────────

function Outro() {
  const [advisorySubmitted, setAdvisorySubmitted] = useState(false);
  const [advisoryName, setAdvisoryName] = useState('');
  const [advisoryEmail, setAdvisoryEmail] = useState('');
  const [advisoryMessage, setAdvisoryMessage] = useState('');
  const [accessSubmitted, setAccessSubmitted] = useState(false);
  const [accessName, setAccessName] = useState('');
  const [accessEmail, setAccessEmail] = useState('');
  const [accessTerms, setAccessTerms] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL || 'https://3yslsbk5n1.execute-api.us-east-1.amazonaws.com/dev';

  const handleAdvisorySubmit = async () => {
    if (!advisoryName.trim() || !advisoryEmail.trim()) return;
    try {
      await fetch(API_BASE + '/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: advisoryName.trim(), email: advisoryEmail.trim(), subject: 'Advisory Request — ' + advisoryName.trim(), message: advisoryMessage.trim() || 'Interested in advisory services.' }),
      });
      setAdvisorySubmitted(true);
    } catch { setAdvisorySubmitted(true); }
  };

  const handleAccessSubmit = async () => {
    if (!accessName.trim() || !accessEmail.trim() || !accessTerms) return;
    try {
      await fetch(API_BASE + '/early-access', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: accessName.trim(), email: accessEmail.trim(), message: 'Early access request from demo.' }),
      });
      setAccessSubmitted(true);
    } catch { setAccessSubmitted(true); }
  };

  return (
    <Box sx={{ py: 4 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif', color: '#2D6E6A', mb: 1 }}>
          Built solo with AI
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.secondary', mb: 3 }}>
          by{' '}
          <Typography
            component="a"
            href="https://www.linkedin.com/in/lcharles"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: '#0A66C2', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            Lourdes Charles
          </Typography>
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto', mb: 4, lineHeight: 1.7 }}>
          Percoro is a full-stack social travel platform — React frontend, AWS Lambda backend, PostgreSQL, AI-powered features — built by one person using Claude as a development partner.
        </Typography>
      </Box>
      {/* Blog Links */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>Read the Build Story</Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button variant="outlined" href="https://www.linkedin.com/posts/lcharles_agenticai-buildinpublic-startups-activity-7424489364732325888-DUoK?utm_source=share&utm_medium=member_desktop&rcm=ACoAAAA3hEcB2Xfzx8W1wcY35uCogvdOo7j45kE" target="_blank"
            sx={{ borderColor: '#0A66C2', color: '#0A66C2', borderRadius: '20px', textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' }}>
            Blog 1: The 72-Hour Build
          </Button>
          <Button variant="outlined" href="https://www.linkedin.com/posts/lcharles_startups-siphals-ai-activity-7432107141844463616-0SUh?utm_source=share&utm_medium=member_desktop&rcm=ACoAAAA3hEcB2Xfzx8W1wcY35uCogvdOo7j45kE" target="_blank"
            sx={{ borderColor: '#0A66C2', color: '#0A66C2', borderRadius: '20px', textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' }}>
            Blog 2: The PostgreSQL Migration
          </Button>
          <Button variant="outlined" href="https://www.linkedin.com/posts/lcharles_buildinpublic-agenticai-techleadership-activity-7465401488480284672-haOE?utm_source=share&utm_medium=member_desktop&rcm=ACoAAAA3hEcB2Xfzx8W1wcY35uCogvdOo7j45kE" target="_blank"
            sx={{ borderColor: '#0A66C2', color: '#0A66C2', borderRadius: '20px', textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' }}>
            Blog 3: The AI Talent Pipeline
          </Button>
        </Box>
      </Box>
      {/* Two CTA cards side by side */}
      <Grid container spacing={3} justifyContent="center">
        {/* CTA 1: Advisory Service */}
        <Grid item xs={12} sm={6}>
          <Card sx={{ borderRadius: 3, p: 2.5, textAlign: 'left', border: '2px solid #2D6E6A', height: '100%' }}>
            {advisorySubmitted ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CheckCircle sx={{ color: '#4CAF50', fontSize: 40, mb: 1 }} />
                <Typography variant="h6" fontWeight={700}>Request Submitted!</Typography>
                <Typography variant="body2" color="text.secondary">We will be in touch shortly.</Typography>
              </Box>
            ) : (
              <>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5, fontFamily: 'Poppins, sans-serif', color: '#2D6E6A' }}>AI Advisory Services</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Need help bringing AI into your organization? Tell us about your goals.
                </Typography>
                <TextField fullWidth size="small" label="Your Name" value={advisoryName} onChange={(e) => setAdvisoryName(e.target.value)} sx={{ mb: 1.5 }} />
                <TextField fullWidth size="small" label="Email" value={advisoryEmail} onChange={(e) => setAdvisoryEmail(e.target.value)} sx={{ mb: 1.5 }} />
                <TextField fullWidth size="small" label="What AI challenges does your organization face?" value={advisoryMessage} onChange={(e) => setAdvisoryMessage(e.target.value)} multiline rows={2} sx={{ mb: 2 }} />
                <Button fullWidth variant="contained" onClick={handleAdvisorySubmit}
                  disabled={!advisoryName.trim() || !advisoryEmail.trim()}
                  sx={{ bgcolor: '#2D6E6A', borderRadius: '24px', textTransform: 'none', fontWeight: 700, py: 1.2, '&:hover': { bgcolor: '#1F4E4A' } }}>
                  Submit Request
                </Button>
              </>
            )}
          </Card>
        </Grid>
        {/* CTA 2: Request Early Access */}
        <Grid item xs={12} sm={6}>
          <Card sx={{ borderRadius: 3, p: 2.5, textAlign: 'left', border: '1px solid #C8DDD8', height: '100%' }}>
            {accessSubmitted ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CheckCircle sx={{ color: '#4CAF50', fontSize: 40, mb: 1 }} />
                <Typography variant="h6" fontWeight={700}>Request Received!</Typography>
                <Typography variant="body2" color="text.secondary">We'll be in touch about early access.</Typography>
              </Box>
            ) : (
              <>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5, fontFamily: 'Poppins, sans-serif' }}>Request Early Access</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Try Percoro as an early user. Admin-approved access.
                </Typography>
                <TextField fullWidth size="small" label="Your Name" value={accessName} onChange={(e) => setAccessName(e.target.value)} sx={{ mb: 1.5 }} />
                <TextField fullWidth size="small" label="Email" value={accessEmail} onChange={(e) => setAccessEmail(e.target.value)} sx={{ mb: 1.5 }} />
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <Checkbox checked={accessTerms} onChange={(e) => setAccessTerms(e.target.checked)} sx={{ color: '#2D6E6A', '&.Mui-checked': { color: '#2D6E6A' }, pt: 0 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    I understand this is an early access platform and the service may be discontinued at any time.
                  </Typography>
                </Box>
                <Button fullWidth variant="contained" onClick={handleAccessSubmit}
                  disabled={!accessName.trim() || !accessEmail.trim() || !accessTerms}
                  sx={{ bgcolor: '#2D6E6A', borderRadius: '24px', textTransform: 'none', fontWeight: 700, py: 1.2, '&:hover': { bgcolor: '#1F4E4A' } }}>
                  Request Early Access
                </Button>
              </>
            )}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

// ─── USER STEP CONFIG (9 screens) ───────────────────────────────────────────

export const USER_STEPS = [
  { label: 'Why Percoro', component: UserStep_WhyPercoro },
  { label: 'Sign Up', component: UserStep2_Signup },
  { label: 'Home', component: UserStep3_Home },
  { label: 'My Trips', component: UserStep_MyTrips },
  { label: 'Create Trip', component: UserStep4_CreateTrip },
  { label: 'Trip Detail', component: UserStep5_TripDetail },
  { label: 'Invite', component: UserStep_InviteFriends },
  { label: 'Photos', component: UserStep6_Photos },
  { label: 'Community', component: UserStep_CommunityFriends },
];

// ─── ADMIN STEP — LOGIN ─────────────────────────────────────────────────────

function AdminStep_Login() {
  return (
    <Box>
      <Narration text="Admin login — role-based access control. Super admins and admins redirect to the admin dashboard. Every admin action is audit-logged with IP, timestamp, and reason." />
      <Card sx={{ maxWidth: 400, mx: 'auto', borderRadius: 4, overflow: 'hidden' }}>
        <Box sx={{ bgcolor: '#2D6E6A', px: 3, py: 2.5, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '1.3rem', color: 'white' }}>PERCORO</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem' }}>Admin Console</Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          <TextField fullWidth size="small" label="Email" value="alex.morgan@percoro.com" sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
          <TextField fullWidth size="small" label="Password" type="password" value="password123" sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
          <Button fullWidth variant="contained" sx={{ bgcolor: '#2D6E6A', borderRadius: '24px', textTransform: 'none', fontWeight: 700, py: 1.2, mb: 2, '&:hover': { bgcolor: '#1F4E4A' } }}>
            Sign In
          </Button>
          <Box sx={{ bgcolor: '#E8F5F3', borderRadius: 2, p: 2, textAlign: 'center' }}>
            <CheckCircle sx={{ color: '#4CAF50', fontSize: 20, mr: 0.5, verticalAlign: 'middle' }} />
            <Typography variant="body2" sx={{ display: 'inline', fontWeight: 600, color: '#2D6E6A' }}>
              Role: super_admin — redirecting to dashboard
            </Typography>
          </Box>
          <Box sx={{ mt: 2, p: 1.5, bgcolor: '#F5F5F5', borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Role-based access:</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>super_admin — full dashboard access</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>admin — read-only + support</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>user — redirected to home</Typography>
          </Box>
        </Box>
      </Card>
    </Box>
  );
}

// ─── ADMIN STEP — AI COST TRACKER ───────────────────────────────────────────

function AdminStep_AICost() {
  return (
    <Box>
      <Narration text="AI Operations — real-time cost tracking, invocation logging, and per-feature spend breakdown. Every Bedrock call is logged with tokens, cost, duration, and status." />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>AI Operations</Typography>
          <Typography variant="body2" color="text.secondary">Spend, latency, and reliability across all AI-powered features</Typography>
        </Box>
        <Box sx={{ minWidth: 140, px: 1.5, py: 0.75, border: '1px solid #ccc', borderRadius: 1, fontSize: '0.85rem', color: '#333' }}>
          Last 7 days
        </Box>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <Card variant="outlined"><CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}><AttachMoney sx={{ color: '#2D6E6A' }} /><Typography variant="body2" color="text.secondary">Total Spend</Typography></Box>
          <Typography variant="h4" fontWeight={700}>$14.82</Typography>
          <Typography variant="caption" color="text.secondary">12,847 in / 4,203 out tokens</Typography>
        </CardContent></Card>
        <Card variant="outlined"><CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}><AutoAwesome sx={{ color: '#2D6E6A' }} /><Typography variant="body2" color="text.secondary">Invocations</Typography></Box>
          <Typography variant="h4" fontWeight={700}>187</Typography>
          <Typography variant="caption" color="text.secondary">182 success / 3 error</Typography>
        </CardContent></Card>
        <Card variant="outlined"><CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}><Notifications sx={{ color: '#2D6E6A' }} /><Typography variant="body2" color="text.secondary">Error Rate</Typography></Box>
          <Typography variant="h4" fontWeight={700}>1.6%</Typography>
          <Typography variant="caption" color="text.secondary">0 disabled / 2 rate-limited</Typography>
        </CardContent></Card>
        <Card variant="outlined"><CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}><Settings sx={{ color: '#2D6E6A' }} /><Typography variant="body2" color="text.secondary">Avg Latency</Typography></Box>
          <Typography variant="h4" fontWeight={700}>842ms</Typography>
          <Typography variant="caption" color="text.secondary">Successful invocations only</Typography>
        </CardContent></Card>
      </Box>
      <Card variant="outlined" sx={{ mb: 3 }}><CardContent>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>By Feature</Typography>
        <Table size="small"><TableHead><TableRow>
          <TableCell>Feature</TableCell><TableCell align="right">Invocations</TableCell><TableCell align="right">Spend</TableCell><TableCell align="right">Errors</TableCell><TableCell align="right">Avg Latency</TableCell><TableCell align="right">Last Invoked</TableCell>
        </TableRow></TableHead><TableBody>
          {[
            { f: 'ai_trip_planner', inv: 89, spend: '$7.2400', err: 1, lat: '1,204ms', last: 'Jun 2, 2:14 PM' },
            { f: 'ai_photo_analysis', inv: 64, spend: '$4.1800', err: 2, lat: '634ms', last: 'Jun 2, 1:47 PM' },
            { f: 'ai_trip_import', inv: 34, spend: '$3.4000', err: 0, lat: '921ms', last: 'Jun 1, 11:30 AM' },
          ].map((r, i) => (
            <TableRow key={i}>
              <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{r.f}</Typography></TableCell>
              <TableCell align="right">{r.inv}</TableCell>
              <TableCell align="right">{r.spend}</TableCell>
              <TableCell align="right">{r.err > 0 ? <Chip label={r.err} size="small" color="error" /> : '0'}</TableCell>
              <TableCell align="right">{r.lat}</TableCell>
              <TableCell align="right"><Typography variant="caption" color="text.secondary">{r.last}</Typography></TableCell>
            </TableRow>
          ))}
        </TableBody></Table>
      </CardContent></Card>
      <Card variant="outlined"><CardContent>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Recent Invocations</Typography>
        <Table size="small"><TableHead><TableRow>
          <TableCell>When</TableCell><TableCell>Feature</TableCell><TableCell>Surface</TableCell><TableCell>Status</TableCell><TableCell align="right">Cost</TableCell><TableCell align="right">Latency</TableCell>
        </TableRow></TableHead><TableBody>
          {[
            { when: 'Jun 2, 2:14 PM', f: 'ai_trip_planner', surf: 'embedded', status: 'success', cost: '$0.1247', lat: '1,204ms' },
            { when: 'Jun 2, 1:47 PM', f: 'ai_photo_analysis', surf: 'embedded', status: 'success', cost: '$0.0534', lat: '634ms' },
            { when: 'Jun 2, 12:30 PM', f: 'ai_trip_planner', surf: 'embedded', status: 'success', cost: '$0.0892', lat: '987ms' },
            { when: 'Jun 2, 11:15 AM', f: 'ai_photo_analysis', surf: 'embedded', status: 'rate_limited', cost: '$0.0000', lat: '12ms' },
            { when: 'Jun 1, 4:22 PM', f: 'ai_trip_import', surf: 'embedded', status: 'error', cost: '$0.0000', lat: '3,421ms' },
          ].map((r, i) => (
            <TableRow key={i}>
              <TableCell><Typography variant="caption">{r.when}</Typography></TableCell>
              <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{r.f}</Typography></TableCell>
              <TableCell><Typography variant="caption" color="text.secondary">{r.surf}</Typography></TableCell>
              <TableCell><Chip label={r.status} size="small" color={r.status === 'success' ? 'success' : r.status === 'error' ? 'error' : 'warning'} /></TableCell>
              <TableCell align="right"><Typography variant="caption">{r.cost}</Typography></TableCell>
              <TableCell align="right"><Typography variant="caption">{r.lat}</Typography></TableCell>
            </TableRow>
          ))}
        </TableBody></Table>
      </CardContent></Card>
    </Box>
  );
}

// ─── ADMIN STEP — EARLY ACCESS ──────────────────────────────────────────────

function AdminStep_EarlyAccess() {
  const [filter, setFilter] = useState('pending');
  const allRequests = [
    { name: 'Sarah Kim', email: 'sarah.k@startup.io', message: 'Saw the demo, love the concept!', date: 'Jun 2, 2026', status: 'pending' },
    { name: 'David Park', email: 'david@enterprise.com', message: 'Interested for our travel team', date: 'Jun 1, 2026', status: 'pending' },
    { name: 'Alex Rivera', email: 'alex.r@email.com', message: 'Early access please!', date: 'May 30, 2026', status: 'approved' },
    { name: 'Morgan Lee', email: 'morgan@design.co', message: '', date: 'May 28, 2026', status: 'rejected' },
  ];
  const filtered = allRequests.filter(r => r.status === filter);
  return (
    <Box>
      <Narration text="Early Access management — admin-controlled signup. Review requests, approve or reject. Approved users receive an email with a signup link. The signup Lambda enforces the gate." />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>Early Access Requests</Typography>
        <ToggleButtonGroup value={filter} exclusive onChange={(_, v) => v && setFilter(v)} size="small">
          <ToggleButton value="pending">Pending</ToggleButton>
          <ToggleButton value="approved">Approved</ToggleButton>
          <ToggleButton value="rejected">Rejected</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table><TableHead><TableRow sx={{ bgcolor: '#F5F5F5' }}>
          <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
          {filter === 'pending' && <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>}
        </TableRow></TableHead><TableBody>
          {filtered.map((req, i) => (
            <TableRow key={i} hover>
              <TableCell><Typography variant="body2" fontWeight={600}>{req.name}</Typography></TableCell>
              <TableCell><Typography variant="body2" color="text.secondary">{req.email}</Typography></TableCell>
              <TableCell><Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{req.message || '—'}</Typography></TableCell>
              <TableCell><Typography variant="caption">{req.date}</Typography></TableCell>
              {filter === 'pending' && (
                <TableCell align="right">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button size="small" variant="contained" startIcon={<CheckCircle sx={{ fontSize: 14 }} />}
                      sx={{ bgcolor: '#2D6E6A', textTransform: 'none', borderRadius: 2, '&:hover': { bgcolor: '#1F4E4A' } }}>Approve</Button>
                    <Button size="small" variant="outlined" startIcon={<Close sx={{ fontSize: 14 }} />}
                      sx={{ textTransform: 'none', borderRadius: 2, color: '#d32f2f', borderColor: '#d32f2f' }}>Reject</Button>
                  </Box>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody></Table>
      </TableContainer>
      <Box sx={{ mt: 2, p: 2, bgcolor: '#F0FAF9', borderRadius: 2, border: '1px solid #C8DDD8' }}>
        <Typography variant="caption" fontWeight={600} color="#2D6E6A">How it works:</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Approve → user gets email with signup link → signup Lambda checks approval → account created
        </Typography>
      </Box>
    </Box>
  );
}

// ─── ADMIN STEP — BUILT FOR ADMINS (intro, value prop) ────────────────

function AdminStep_BuiltForAdmins() {
  const caps = [
    { icon: <People sx={{ fontSize: 26, color: '#2D6E6A' }} />, title: 'User management', body: 'Search, review, and manage every account with role-based access and full audit logging.' },
    { icon: <ToggleOn sx={{ fontSize: 26, color: '#2D6E6A' }} />, title: 'Feature control', body: 'Turn features on or off instantly with feature flags — no redeploys, full control over the product surface.' },
    { icon: <Support sx={{ fontSize: 26, color: '#2D6E6A' }} />, title: 'Customer support', body: 'A built-in support queue so user issues are tracked, assigned, and resolved in one place.' },
    { icon: <AutoAwesome sx={{ fontSize: 26, color: '#2D6E6A' }} />, title: 'AI analytics', body: 'Real-time spend, latency, and reliability tracking for every AI-powered feature.' },
    { icon: <Campaign sx={{ fontSize: 26, color: '#2D6E6A' }} />, title: 'Notifications', body: 'Broadcast announcements to all users or targeted segments, with delivery tracking.' },
  ];
  return (
    <Box>
      <Box sx={{ background: 'linear-gradient(135deg, #2D6E6A 0%, #1a4a47 50%, #2D6E6A 100%)', borderRadius: 4, p: { xs: 3, sm: 5 }, color: 'white', mb: 3 }}>
        <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: { xs: '1.6rem', sm: '2.2rem' }, mb: 1.5 }}>
          Built with admins in mind
        </Typography>
      </Box>
      <Grid container spacing={2}>
        {caps.map((c, i) => (
          <Grid item xs={12} sm={6} md={4} key={i}>
            <Card variant="outlined" sx={{ borderRadius: 3, height: '100%', borderColor: '#E0ECEA' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ bgcolor: '#E8F0EE', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                  {c.icon}
                </Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ fontFamily: 'Poppins, sans-serif', mb: 0.5 }}>{c.title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{c.body}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

// ─── ADMIN STEP — RESOURCES (Blog / CMS) ─────────────────────────

function AdminStep_Resources() {
  const rows = [
    { title: 'The 72-Hour Build', cat: 'Blog', type: 'External', status: 'Published', order: 1 },
    { title: 'Why I Migrated to PostgreSQL Mid-Build', cat: 'Blog', type: 'External', status: 'Published', order: 2 },
    { title: 'Missing the Launch Date', cat: 'Blog', type: 'External', status: 'Published', order: 3 },
    { title: 'Your AI Investment Deserves Better Guidance', cat: 'Blog', type: 'Hosted', status: 'Draft', order: 0 },
  ];
  const statusChip = (s: string) => s === 'Published'
    ? { bg: '#E8F5F3', fg: '#2D6E6A' } : s === 'Draft' ? { bg: '#FFF3E0', fg: '#B26A00' } : { bg: '#FBE3E3', fg: '#C62828' };
  return (
    <Box>
      <Narration text="Resources — a built-in blog and content manager. Publish posts hosted on Percoro or link out to external articles, with draft/publish/hold lifecycle control. No redeploys to change the public site." />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>Resources</Typography>
        <Button variant="contained" startIcon={<AddCircleOutline />} sx={{ bgcolor: '#2D6E6A', textTransform: 'none', borderRadius: 2, '&:hover': { bgcolor: '#1F4E4A' } }}>New Resource</Button>
      </Box>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small"><TableHead><TableRow sx={{ bgcolor: '#F5F5F5' }}>
          <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
          <TableCell sx={{ fontWeight: 600 }} align="center">Order</TableCell>
          <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
        </TableRow></TableHead><TableBody>
          {rows.map((r, i) => { const c = statusChip(r.status); return (
            <TableRow key={i} hover>
              <TableCell><Typography variant="body2" fontWeight={600}>{r.title}</Typography></TableCell>
              <TableCell>{r.cat}</TableCell>
              <TableCell>{r.type}</TableCell>
              <TableCell><Chip label={r.status} size="small" sx={{ bgcolor: c.bg, color: c.fg, fontWeight: 600 }} /></TableCell>
              <TableCell align="center">{r.order}</TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                  <Button size="small" variant="outlined" sx={{ textTransform: 'none', borderRadius: 2, color: '#2D6E6A', borderColor: '#2D6E6A', fontSize: '0.72rem' }}>Edit</Button>
                  <Button size="small" variant="outlined" sx={{ textTransform: 'none', borderRadius: 2, color: '#2D6E6A', borderColor: '#2D6E6A', fontSize: '0.72rem' }}>{r.status === 'Published' ? 'Hold' : 'Publish'}</Button>
                  <Button size="small" variant="outlined" sx={{ textTransform: 'none', borderRadius: 2, color: '#d32f2f', borderColor: '#d32f2f', fontSize: '0.72rem' }}>Delete</Button>
                </Box>
              </TableCell>
            </TableRow>
          ); })}
        </TableBody></Table>
      </TableContainer>
    </Box>
  );
}

// ─── ADMIN STEP — STAFF & RBAC ────────────────────────────

function AdminStep_StaffRBAC() {
  const staff = [
    { name: 'Alex Morgan', email: 'alex.morgan@percoro.com', role: 'super_admin', desc: 'Full dashboard access — all sections, all actions', color: '#2D6E6A' },
    { name: 'Maya Johnson', email: 'maya.johnson@percoro.com', role: 'admin', desc: 'Read access + support — no destructive actions', color: '#3949ab' },
    { name: 'David Park', email: 'david.support@percoro.com', role: 'support', desc: 'Support queue only — view tickets, respond to users', color: '#8e24aa' },
  ];
  return (
    <Box>
      <Narration text="Staff & RBAC — role-based access control for your team. Assign super_admin, admin, or support roles; each role sees only what it should. Every staff action is audit-logged." />
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Staff &amp; Roles</Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small"><TableHead><TableRow sx={{ bgcolor: '#F5F5F5' }}>
          <TableCell sx={{ fontWeight: 600 }}>Staff Member</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Access</TableCell>
        </TableRow></TableHead><TableBody>
          {staff.map((s, i) => (
            <TableRow key={i} hover>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ width: 28, height: 28, bgcolor: s.color, fontSize: '0.75rem' }}>{s.name[0]}</Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{s.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{s.email}</Typography>
                  </Box>
                </Box>
              </TableCell>
              <TableCell><Chip label={s.role} size="small" sx={{ bgcolor: s.color, color: 'white', fontWeight: 600, fontFamily: 'monospace' }} /></TableCell>
              <TableCell><Typography variant="caption" color="text.secondary">{s.desc}</Typography></TableCell>
            </TableRow>
          ))}
        </TableBody></Table>
      </TableContainer>
      <Box sx={{ mt: 2, p: 2, bgcolor: '#F0FAF9', borderRadius: 2, border: '1px solid #C8DDD8' }}>
        <Typography variant="caption" fontWeight={600} color="#2D6E6A">Merge authority levels:</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          super_admin (level 2) → admin (level 1) → support (level 0). Higher levels inherit all lower permissions.
        </Typography>
      </Box>
    </Box>
  );
}

// ─── ADMIN STEP — ADMIN HOME (mirrors the real dashboard landing) ──────

function AdminStep_Home() {
  const cards = [
    { title: 'User Support', icon: <Support sx={{ color: '#2D6E6A' }} />, value: '3', caption: '1 SLA-breached · 2 urgent', tone: 'error' },
    { title: 'System Health', icon: <Settings sx={{ color: '#2D6E6A' }} />, value: 'Operational', caption: 'All checks passing', tone: 'calm' },
    { title: 'Access Requests', icon: <PersonAdd sx={{ color: '#2D6E6A' }} />, value: '2', caption: '2 awaiting review', tone: 'attention' },
    { title: 'AI Warnings', icon: <AutoAwesome sx={{ color: '#2D6E6A' }} />, value: '1.6%', caption: 'Error rate nominal', tone: 'calm' },
  ];
  const toneColor = (t: string) => (t === 'error' ? '#C62828' : t === 'attention' ? '#B26A00' : '#2D6E6A');
  return (
    <Box>
      <Narration text="Admins land on a live snapshot of what needs attention — open support tickets, system health, pending access requests, and AI reliability — before drilling into any one area." />
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>What needs your attention</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>A live snapshot across support, system, access, and AI.</Typography>
      <Grid container spacing={2}>
        {cards.map((c, i) => {
          const color = toneColor(c.tone);
          const flagged = c.tone !== 'calm';
          return (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Card variant="outlined" sx={{ borderRadius: 3, height: '100%', borderColor: flagged ? color : '#E0ECEA', borderWidth: flagged ? 2 : 1 }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ bgcolor: '#E8F0EE', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{c.icon}</Box>
                    {flagged && <Chip size="small" label="Attention" sx={{ bgcolor: c.tone === 'error' ? '#FBE3E3' : '#FFF3E0', color, fontWeight: 600, height: 22 }} />}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{c.title}</Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ color, lineHeight: 1.2, my: 0.25 }}>{c.value}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{c.caption}</Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

const ADMIN_STEPS = [
  { label: 'Built for Admins', component: AdminStep_BuiltForAdmins },
  { label: 'Login', component: AdminStep_Login },
  { label: 'Admin Home', component: AdminStep_Home },
  { label: 'Notifications', component: AdminStep2_Notifications },
  { label: 'User Access', component: AdminStep_EarlyAccess },
  { label: 'AI Analytics', component: AdminStep_AICost },
  { label: 'Resources', component: AdminStep_Resources },
  { label: 'Feature Control', component: AdminStep1_Features },
  { label: 'Staff & RBAC', component: AdminStep_StaffRBAC },
];

// ─── MAIN DEMO PAGE ─────────────────────────────────────────────────────────

// ─── AUTO-FILL HOOK (fields pre-filled; clicking re-types for effect) ────────
function useAutoFill(initial: string) {
  const [val, setVal] = React.useState(initial);
  const [filling, setFilling] = React.useState(false);
  const animate = () => {
    if (filling) return;
    setFilling(true);
    setVal('');
    let i = 0;
    const timer = setInterval(() => {
      i++;
      setVal(initial.slice(0, i));
      if (i >= initial.length) { clearInterval(timer); setFilling(false); }
    }, 28);
  };
  return { val, animate, filling };
}

// ─── FLOW: SIGN UP ──────────────────────────────────────────────────────────
function FlowSignup({ onNext }: any) {
  const name = useAutoFill('Maya Johnson');
  const email = useAutoFill('maya.johnson@email.com');
  const pwd = useAutoFill('password');
  return (
    <Box>
      <Narration text="Getting in takes seconds — email or Google. We keep the front door simple so you get to the part that matters: your trips." />
      <Card sx={{ maxWidth: 420, mx: 'auto', borderRadius: 4, p: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h5" fontWeight={700} color="#2D6E6A" sx={{ fontFamily: 'Poppins, sans-serif' }}>PERCORO</Typography>
          <Typography variant="body2" color="text.secondary">Create your account</Typography>
        </Box>
        <Button fullWidth variant="outlined" sx={{ mb: 2, borderRadius: '30px', textTransform: 'none', py: 1.2, borderColor: '#dadce0' }}>
          Continue with Google
        </Button>
        <Divider sx={{ my: 2 }}>or continue with email</Divider>
        <TextField fullWidth size="small" label="Full Name" value={name.val} onClick={name.animate}
          InputProps={{ readOnly: true }} sx={{ mb: 1.5, ...interactiveSx }} />
        <TextField fullWidth size="small" label="Email" value={email.val} onClick={email.animate}
          InputProps={{ readOnly: true }} sx={{ mb: 1.5, ...interactiveSx }} />
        <TextField fullWidth size="small" label="Password" type="password" value={pwd.val} onClick={pwd.animate}
          InputProps={{ readOnly: true }} sx={{ mb: 2, ...interactiveSx }} />
        <Button fullWidth variant="contained" onClick={onNext}
          sx={{ bgcolor: '#2D6E6A', borderRadius: '30px', textTransform: 'none', py: 1.2, fontWeight: 700, '&:hover': { bgcolor: '#1F4E4A' } }}>
          Sign In
        </Button>
      </Card>
    </Box>
  );
}

// ─── FLOW: HOME ─────────────────────────────────────────────────────────────
function FlowHome({ go }: any) {
  const ANNOUNCEMENTS = 4;
  const dash = [
    { count: 1, label: 'Friend invites', color: '#e53935', icon: <PersonAdd sx={{ color: '#e53935', fontSize: 16 }} />, target: 'friends', disabled: false },
    { count: ANNOUNCEMENTS, label: 'Announcements', color: '#2D6E6A', icon: <Notifications sx={{ color: '#2D6E6A', fontSize: 16 }} />, target: 'announcements', disabled: false },
    { count: 2, label: 'Trip reminders', color: '#f57c00', icon: <Flight sx={{ color: '#f57c00', fontSize: 16 }} />, target: 'mytrips', disabled: false },
    { count: 0, label: 'Friend updates', color: '#8e24aa', icon: <People sx={{ color: '#8e24aa', fontSize: 16 }} />, target: '', disabled: true },
  ];
  const actions = [
    { title: 'My Trips', sub: 'View and manage', icon: <TravelExplore sx={{ fontSize: 24 }} />, iconColor: '#2D6E6A', bgColor: '#C8DDD8', target: 'mytrips' },
    { title: 'Import a Trip', sub: 'Upload booking PDF', icon: <FlightTakeoff sx={{ fontSize: 24 }} />, iconColor: '#e53935', bgColor: '#F0E0D0', target: 'createTrip' },
    { title: 'Companions', sub: 'Invite companions', icon: <ConnectWithoutContact sx={{ fontSize: 24 }} />, iconColor: '#3949ab', bgColor: '#D0D8E8', target: '', disabled: true },
    { title: 'Friends', sub: 'Connect and share', icon: <People sx={{ fontSize: 24 }} />, iconColor: '#8e24aa', bgColor: '#D8D0E8', target: 'friends' },
    { title: 'My Photos', sub: 'Travel gallery', icon: <PhotoLibrary sx={{ fontSize: 24 }} />, iconColor: '#f57c00', bgColor: '#EDE0C8', target: 'myphotos' },
    { title: 'Community', sub: 'Explore trips', icon: <Explore sx={{ fontSize: 24 }} />, iconColor: '#0288d1', bgColor: '#C8DCE8', target: 'community' },
  ];
  return (
    <Box>
      <Narration text="Everything starts here — your trips, your circle, and what needs your attention, in one place. No hunting across apps." />
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0', px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: '12px 12px 0 0' }}>
        <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, color: '#2D6E6A', fontSize: '1.1rem' }}>PERCORO</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CloudUpload sx={{ color: '#666', fontSize: 20, ...interactiveSx }} onClick={() => go('photos')} />
          <Avatar sx={{ width: 32, height: 32, bgcolor: '#2D6E6A', fontSize: '0.8rem' }}>M</Avatar>
          <Settings sx={{ color: '#666', fontSize: 20 }} />
        </Box>
      </Box>
      <Box sx={{ bgcolor: 'white', borderBottom: '2px solid #2D6E6A', display: 'flex', justifyContent: 'center', gap: 4, py: 1 }}>
        {[{ l: 'My Trips', t: 'mytrips' }, { l: 'My Photos', t: 'photos' }, { l: 'Community', t: 'community' }].map((tab, i) => (
          <Box key={tab.l} onClick={() => go(tab.t)} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: i === 0 ? '#2D6E6A' : '#999', borderBottom: i === 0 ? '3px solid #2D6E6A' : 'none', pb: 0.5, cursor: 'pointer' }}>
            {i === 0 && <TravelExplore sx={{ fontSize: 18 }} />}
            {i === 1 && <PhotoLibrary sx={{ fontSize: 18 }} />}
            {i === 2 && <Explore sx={{ fontSize: 18 }} />}
            <Typography variant="body2" fontWeight={i === 0 ? 600 : 400}>{tab.l}</Typography>
          </Box>
        ))}
      </Box>
      <Box sx={{ background: 'linear-gradient(135deg, #2D6E6A 0%, #007a6e 100%)', px: 3, py: 3, color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ fontFamily: 'Poppins, sans-serif', mb: 0.5 }}>Welcome back, Maya.</Typography>
            <Typography sx={{ opacity: 0.9, fontSize: '0.9rem' }}>Here's what's happening with your trips and friends.</Typography>
          </Box>
          <Badge badgeContent={ANNOUNCEMENTS} color="error">
            <Box onClick={() => go('announcements')} sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '50%', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid rgba(255,255,255,0.5)', cursor: 'pointer' }}>
              <Notifications sx={{ color: 'white', fontSize: 22 }} />
            </Box>
          </Badge>
        </Box>
      </Box>
      <Box sx={{ px: 2, py: 2, bgcolor: '#F8FAFB' }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, fontFamily: 'Poppins, sans-serif' }}>Your dashboard</Typography>
        <Grid container spacing={1.5} sx={{ mb: 3 }}>
          {dash.map((n, i) => (
            <Grid item xs={3} key={i}>
              <Card onClick={() => { if (!n.disabled) go(n.target); }} sx={{ borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: n.count > 0 ? '1px solid #f0e0d0' : '1px solid #f0f0f0', opacity: n.disabled ? 0.5 : 1, ...(n.disabled ? { cursor: 'default' } : interactiveSx) }}>
                <CardContent sx={{ textAlign: 'center', py: 1.5, px: 1, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ bgcolor: 'white', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 0.5, border: '1px solid #e0e0e0' }}>{n.icon}</Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: n.count > 0 ? n.color : '#bdbdbd', fontSize: '1.1rem' }}>{n.count}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', lineHeight: 1.2, display: 'block' }}>{n.label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, fontFamily: 'Poppins, sans-serif' }}>Quick actions</Typography>
        <Grid container spacing={1.5}>
          {actions.map((card, i) => (
            <Grid item xs={4} key={i}>
              <Card onClick={() => { if (!(card as any).disabled) go(card.target); }} sx={{ borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', opacity: (card as any).disabled ? 0.5 : 1, ...((card as any).disabled ? { cursor: 'default' } : interactiveSx) }}>
                <CardContent sx={{ textAlign: 'center', py: 2, px: 1, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ bgcolor: 'white', borderRadius: '50%', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1, color: card.iconColor, border: '2px solid ' + card.iconColor, boxShadow: '0 0 0 4px ' + card.bgColor }}>{card.icon}</Box>
                  <Typography variant="caption" fontWeight={600} sx={{ display: 'block' }}>{card.title}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>{card.sub}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Card sx={{ borderRadius: '16px', mt: 2, border: '1px solid #2D6E6A', boxShadow: 'none' }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
            <Box sx={{ bgcolor: 'white', borderRadius: '50%', width: 40, height: 40, minWidth: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #2D6E6A' }}>
              <WorkspacePremium sx={{ color: '#2D6E6A', fontSize: 20 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={600}>You're on the Pro Plan</Typography>
              <Typography variant="caption" color="text.secondary">15 GB storage, trip companions, expiration control, and more.</Typography>
            </Box>
            <Button size="small" variant="outlined" onClick={() => go('plans')} sx={{ borderColor: '#2D6E6A', color: '#2D6E6A', borderRadius: '20px', textTransform: 'none', fontWeight: 600, ...interactiveSx }}>View Plans</Button>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

// ─── FLOW: MY TRIPS LIST (Tokyo + Lisbon; click opens that trip) ─────────────
function FlowMyTrips({ go }: any) {
  const myTrips = DEMO_TRIPS.slice(1); // Tokyo, Lisbon (Safari is created later)
  return (
    <Box>
      <Narration text="Every trip you've planned or taken, in one library. Find any trip by name, place, or confirmation code — instead of digging through your inbox." />
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, color: '#2D6E6A', flexGrow: 1 }}>My Trips</Typography>
        <Button variant="contained" size="small" startIcon={<AddCircleOutline />} onClick={() => go('createTrip')}
          sx={{ bgcolor: '#2D6E6A', borderRadius: '24px', textTransform: 'none', fontWeight: 600, px: 2, '&:hover': { bgcolor: '#1F4E4A' } }}>Create Trip</Button>
        <Button variant="outlined" size="small" sx={{ borderColor: '#e0e0e0', color: '#555', borderRadius: '24px', textTransform: 'none', fontWeight: 600, px: 2 }}>Filter / Export</Button>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, mb: 2, borderRadius: '28px', bgcolor: '#f5f5f5', border: '1px solid #eee' }}>
        <Typography sx={{ color: '#bbb', fontSize: '0.88rem' }}>Search by trip name, destination, or reservation code...</Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
        {myTrips.map((trip, i) => (
          <Box key={i} onClick={() => go('tripDetail', trip)} sx={{ borderRadius: '16px', bgcolor: '#fff', borderLeft: '5px solid #2D6E6A', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden', cursor: 'pointer', '&:hover': { boxShadow: '0 6px 20px rgba(45,110,106,0.18)', transform: 'translateY(-2px)', outline: '1px solid rgba(45,110,106,0.35)' }, transition: 'all 0.2s' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 1.5, pb: 0.5 }}>
              <Checkbox size="small" sx={{ p: 0, color: '#ddd' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label={trip.status === 'planning' ? 'Planning' : 'Upcoming'} size="small" color={trip.status === 'planning' ? 'info' : 'warning'} sx={{ height: 22, fontSize: '0.68rem', fontWeight: 600 }} />
                <Typography sx={{ color: '#ccc' }}>⋮</Typography>
              </Box>
            </Box>
            <Box sx={{ px: 2, pb: 1.5 }}>
              <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '1rem', color: '#1a1a1a', mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{trip.title}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <LocationOn sx={{ fontSize: 15, color: '#aaa' }} />
                <Typography sx={{ fontSize: '0.82rem', color: '#666', fontWeight: 500 }}>{trip.destination}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, color: '#888' }}>
                <CalendarToday sx={{ fontSize: 13 }} />
                <Typography sx={{ fontSize: '0.78rem' }}>{trip.startDate} - {trip.endDate}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35, px: 0.75, py: 0.2, borderRadius: '6px', bgcolor: 'rgba(45,110,106,0.1)' }}>
                  <Typography sx={{ fontSize: '0.64rem', fontWeight: 600, color: '#2D6E6A' }}>Individual</Typography>
                </Box>
                <Box sx={{ flex: 1 }} />
                <Lock sx={{ fontSize: 13, color: '#ccc' }} />
                {trip.companions.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, color: '#2D6E6A' }}>
                    <People sx={{ fontSize: 13 }} />
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 600 }}>{trip.companions.length}</Typography>
                  </Box>
                )}
              </Box>
            </Box>
            <Box sx={{ px: 2, pb: 1, pt: 0.5, borderTop: '1px solid #f5f5f5' }}>
              <Button fullWidth variant="outlined" size="small" startIcon={<CloudUpload sx={{ fontSize: 16 }} />} onClick={(e: any) => { e.stopPropagation(); go('tripDetail', trip); }}
                sx={{ borderColor: '#2D6E6A', color: '#2D6E6A', borderRadius: '10px', textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', py: 0.7 }}>Open Trip</Button>
            </Box>
          </Box>
        ))}
      </Box>
      <Typography variant="caption" sx={{ color: '#bbb', mt: 2, display: 'block' }}>{myTrips.length} trips</Typography>
    </Box>
  );
}

// ─── FLOW: CREATE TRIP (auto-fill fields; creates Safari) ────────────────────
function FlowCreateTrip({ go }: any) {
  const title = useAutoFill('East Africa Safari');
  const dest = useAutoFill('Kenya & Tanzania');
  const desc = useAutoFill('Game drives across the Maasai Mara and Serengeti, with a balloon safari and Ngorongoro crater day.');
  const safari = DEMO_TRIPS[0];
  return (
    <Box>
      <Narration text="Upload your existing bookings and package your trip in seconds. One complete itinerary — flights, hotels, and activities — without manual entry." />
      <Card sx={{ borderRadius: 3, maxWidth: 500, mx: 'auto' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2, fontFamily: 'Poppins, sans-serif' }}>Create a New Trip</Typography>
          <TextField fullWidth size="small" label="Trip Title" value={title.val} onClick={title.animate} InputProps={{ readOnly: true }} sx={{ mb: 2, ...interactiveSx, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
          <TextField fullWidth size="small" label="Destination" value={dest.val} onClick={dest.animate} InputProps={{ readOnly: true }} sx={{ mb: 2, ...interactiveSx, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}><TextField fullWidth size="small" label="Start Date" type="date" value="2026-08-05" InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="End Date" type="date" value="2026-08-13" InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid>
          </Grid>
          <TextField fullWidth size="small" label="Description (optional)" value={desc.val} onClick={desc.animate} InputProps={{ readOnly: true }} multiline rows={2} sx={{ mb: 2, ...interactiveSx, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
          <Button fullWidth variant="contained" onClick={() => go('tripDetail', safari)} sx={{ bgcolor: '#2D6E6A', borderRadius: 2, textTransform: 'none', fontWeight: 700, py: 1.2, '&:hover': { bgcolor: '#1F4E4A' } }}>Create Trip</Button>
        </CardContent>
      </Card>
    </Box>
  );
}

// ─── FLOW: TRIP DETAIL (parameterized; Share + Upload-photos CTAs) ───────────
function FlowTripDetail({ trip, go }: any) {
  const [tab, setTab] = React.useState(0);
  const [planningOpen, setPlanningOpen] = React.useState(true);
  const items = [trip.flights, trip.hotels, trip.activities];
  const tabMeta = [
    { icon: <Flight sx={{ fontSize: 18 }} />, label: 'Flights', count: trip.flights.length, color: '#2196F3' },
    { icon: <Hotel sx={{ fontSize: 18 }} />, label: 'Hotels', count: trip.hotels.length, color: '#4A6FA5' },
    { icon: <Attractions sx={{ fontSize: 18 }} />, label: 'Activities', count: trip.activities.length, color: '#2D6E6A' },
  ];
  const statusLabel = trip.status === 'planning' ? 'Planning' : trip.status === 'upcoming' ? 'Upcoming' : trip.status;
  return (
    <Box>
      <Narration text="The whole trip in one view — flights, hotels, activities, costs. Share it with the people coming along, or print it for the road." />
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0', px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: '12px 12px 0 0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ArrowBack sx={{ color: '#666', fontSize: 20, ...interactiveSx }} onClick={() => go('mytrips')} />
          <Typography variant="subtitle1" fontWeight={700}>Trip Details</Typography>
        </Box>
        <Chip label={statusLabel} size="small" color="info" sx={{ fontWeight: 600 }} />
      </Box>
      <Card sx={{ borderRadius: 0, borderBottom: '1px solid #e0e0e0', boxShadow: 'none' }}>
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h5" fontWeight={700}>{trip.title}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <LocationOn sx={{ color: '#FF6B6B', fontSize: 18 }} />
                <Typography variant="body1" fontWeight={500}>{trip.destination}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, color: 'text.secondary' }}>
                <CalendarToday sx={{ fontSize: 16 }} />
                <Typography variant="body2">{trip.startDate} - {trip.endDate}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Chip icon={<Lock />} label="Private" size="small" variant="outlined" />
                {trip.companions.length > 0 && (<Chip icon={<People />} label={'Shared with ' + trip.companions.length} size="small" sx={{ bgcolor: 'rgba(0,180,160,0.08)', color: '#2D6E6A' }} />)}
              </Box>
            </Box>
            <Button variant="contained" startIcon={<Share />} size="small" onClick={() => go('share', trip)}
              sx={{ bgcolor: '#2D6E6A', borderRadius: 2, textTransform: 'none', fontWeight: 600, boxShadow: 'none', '&:hover': { bgcolor: '#1F4E4A' } }}>Share Trip</Button>
          </Box>
        </CardContent>
      </Card>
      <Paper sx={{ borderRadius: 0, overflow: 'hidden', boxShadow: 'none', border: '1px solid #e0e0e0', borderTop: 0 }}>
        <Box onClick={() => setPlanningOpen(!planningOpen)} sx={{ bgcolor: '#2D6E6A', color: 'white', px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}>
          <Attractions sx={{ fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight={600}>Trip Planning</Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, ml: 1 }}>({trip.flights.length + trip.hotels.length + trip.activities.length} items)</Typography>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label="Upload disabled in demo" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)', fontSize: '0.65rem' }} />
            <Typography>{planningOpen ? '▲' : '▼'}</Typography>
          </Box>
        </Box>
        {planningOpen && (
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, borderBottom: '1px solid #e0e0e0', mb: 2, pb: 1 }}>
              {tabMeta.map((t, i) => (
                <Box key={i} onClick={() => setTab(i)} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: tab === i ? '#2D6E6A' : '#999', cursor: 'pointer', pb: 0.5, borderBottom: tab === i ? '2px solid #2D6E6A' : 'none' }}>
                  {t.icon}
                  <Typography variant="caption" fontWeight={tab === i ? 600 : 400}>{t.label}</Typography>
                  {t.count > 0 && <Badge badgeContent={t.count} color="secondary" sx={{ ml: 0.5, '& .MuiBadge-badge': { fontSize: '0.6rem', height: 16, minWidth: 16 } }} />}
                </Box>
              ))}
            </Box>
            {items[tab].length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No {tabMeta[tab].label.toLowerCase()} added yet.</Typography>
            ) : items[tab].map((it: any, i: number) => (
              <Card key={i} variant="outlined" sx={{ borderRadius: 2, mb: 1.5 }}>
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {tab === 0 ? <Flight sx={{ color: '#2196F3', fontSize: 20 }} /> : tab === 1 ? <Hotel sx={{ color: '#4A6FA5', fontSize: 20 }} /> : <Attractions sx={{ color: '#2D6E6A', fontSize: 20 }} />}
                    <Box sx={{ flex: 1 }}><Typography variant="subtitle2" fontWeight={600}>{it.title}</Typography><Typography variant="caption" color="text.secondary">{it.date}</Typography></Box>
                    {it.status && <Chip label={it.status} size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />}
                    <Typography variant="caption" fontWeight={600} sx={{ color: '#2D6E6A' }}>{'$' + it.cost}</Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Paper>
      <Card sx={{ borderRadius: 0, bgcolor: 'rgba(0,180,160,0.05)', boxShadow: 'none', border: '1px solid #e0e0e0', borderTop: 0 }}>
        <CardContent sx={{ py: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={4} sx={{ textAlign: 'center' }}><Typography variant="h6" fontWeight={700} color="#2D6E6A">{trip.days}</Typography><Typography variant="caption" color="text.secondary">Days</Typography></Grid>
            <Grid item xs={4} sx={{ textAlign: 'center' }}><Typography variant="h6" fontWeight={700} color="#2D6E6A">{trip.items}</Typography><Typography variant="caption" color="text.secondary">Items</Typography></Grid>
            <Grid item xs={4} sx={{ textAlign: 'center' }}><Typography variant="h6" fontWeight={700} color="#2D6E6A">{'$' + trip.cost.toLocaleString()}</Typography><Typography variant="caption" color="text.secondary">Est. Total</Typography></Grid>
          </Grid>
        </CardContent>
      </Card>
      <Card sx={{ borderRadius: '0 0 12px 12px', boxShadow: 'none', border: '1px solid #2D6E6A', borderTop: 0, bgcolor: '#F0F7F6' }}>
        <CardContent sx={{ py: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <PhotoLibrary sx={{ color: '#2D6E6A' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>Add photos to this trip</Typography>
            <Typography variant="caption" color="text.secondary">Upload your memories — each with its own privacy level.</Typography>
          </Box>
          <Button variant="contained" startIcon={<CloudUpload />} onClick={() => go('uploading', trip)}
            sx={{ bgcolor: '#2D6E6A', borderRadius: 2, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#1F4E4A' } }}>Upload Photos</Button>
        </CardContent>
      </Card>
    </Box>
  );
}

// ─── FLOW: SHARE (companion toggle reveals expiry; Send returns to detail) ───
function FlowShare({ trip, go }: any) {
  const [companion, setCompanion] = React.useState(true);
  const [expiry, setExpiry] = React.useState(3);
  const [picked, setPicked] = React.useState<{[k: string]: boolean}>({ 'Alex Rivera': true, 'Sam Chen': true, 'Priya Patel': false });
  const friends = ['Alex Rivera', 'Sam Chen', 'Priya Patel'];
  const count = Object.values(picked).filter(Boolean).length;
  return (
    <Box>
      <Narration text="Share with the people coming along. Add them as travel companions so they can contribute photos — and set when their access expires." />
      <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <ArrowBack sx={{ color: '#666', ...interactiveSx }} onClick={() => go('tripDetail', trip)} />
          <Share sx={{ color: '#2D6E6A' }} />
          <Typography variant="h6" fontWeight={700}>Share {trip.title}</Typography>
        </Box>
        <Box sx={{ p: 2.5 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Share with friends</Typography>
          {friends.map((name, i) => (
            <Box key={i} onClick={() => setPicked(p => ({ ...p, [name]: !p[name] }))} sx={{ display: 'flex', alignItems: 'center', py: 0.75, borderRadius: 2, px: 1, cursor: 'pointer', '&:hover': { bgcolor: '#f5f5f5' } }}>
              <Checkbox size="small" checked={!!picked[name]} sx={{ color: '#2D6E6A', '&.Mui-checked': { color: '#2D6E6A' } }} />
              <Avatar sx={{ width: 32, height: 32, bgcolor: ['#2D6E6A', '#3949ab', '#8e24aa'][i], fontSize: '0.8rem', mr: 1 }}>{name[0]}</Avatar>
              <Box>
                <Typography variant="body2" fontWeight={500}>{name}</Typography>
                <Typography variant="caption" color="text.secondary">{name.toLowerCase().replace(' ', '.') + '@email.com'}</Typography>
              </Box>
            </Box>
          ))}
          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box>
              <Typography variant="subtitle2" fontWeight={600}>Invite as Travel Companion</Typography>
              <Typography variant="caption" color="text.secondary">Companion can add photos and contribute to the trip</Typography>
            </Box>
            <Checkbox checked={companion} onChange={() => setCompanion(!companion)} sx={{ color: '#2D6E6A', '&.Mui-checked': { color: '#2D6E6A' } }} />
          </Box>
          {companion && (
            <Box sx={{ px: 1.5, py: 1.5, bgcolor: '#F0F7F6', borderRadius: 2, mb: 2, border: '1px solid #C8DDD8' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>Access expires</Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 0.75 }}>
                {[1, 3, 7].map(d => (
                  <Chip key={d} label={d + ' day' + (d > 1 ? 's' : '') + ' before'} size="small" onClick={() => setExpiry(d)}
                    variant={expiry === d ? 'filled' : 'outlined'} sx={expiry === d ? { bgcolor: '#2D6E6A', color: 'white', cursor: 'pointer' } : { cursor: 'pointer', borderColor: '#2D6E6A', color: '#2D6E6A' }} />
                ))}
              </Box>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button onClick={() => go('tripDetail', trip)} sx={{ textTransform: 'none', color: '#999' }}>Cancel</Button>
            <Button variant="contained" startIcon={<Share />} onClick={() => go('home')}
              sx={{ bgcolor: '#2D6E6A', borderRadius: 2, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#1F4E4A' } }}>Send to {count} {count === 1 ? 'Friend' : 'Friends'}</Button>
          </Box>
        </Box>
      </Card>
    </Box>
  );
}

// ─── FLOW: UPLOADING (shows upload working, then advances to photos) ─────────
function FlowUploading({ trip, go }: any) {
  const [progress, setProgress] = React.useState(0);
  React.useEffect(() => {
    const timer = setInterval(() => setProgress(p => { if (p >= 100) { clearInterval(timer); setTimeout(() => go('photos', trip), 500); return 100; } return p + 8; }), 90);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Box>
      <Narration text="Uploading your photos. Percoro's AI tags and captions each one as it lands — then you set the privacy level." />
      <Card sx={{ borderRadius: 3, maxWidth: 460, mx: 'auto', p: 3, textAlign: 'center' }}>
        <CloudUpload sx={{ fontSize: 48, color: '#2D6E6A', mb: 2 }} />
        <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>Uploading photos…</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Lioness at dawn, Maasai Mara</Typography>
        <Box sx={{ borderRadius: 3, overflow: 'hidden', mb: 2 }}>
          <PhotoPlaceholder label="Lioness at dawn, Maasai Mara" src="/demo/ea-1.jpg" height={160} borderRadius={12} />
        </Box>
        <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { bgcolor: '#2D6E6A' } }} />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>{progress}% · AI tagging in progress</Typography>
      </Card>
    </Box>
  );
}

// ─── FLOW: PHOTOS (per-photo privacy select; Next -> community) ──────────────
function FlowPhotos({ trip, go, photos, setPhotos }: any) {
  const visStyle: any = {
    public: { label: 'Public', bg: '#E8F5E9', color: '#4CAF50', icon: <Public sx={{ fontSize: 12 }} /> },
    bffs: { label: 'BFFs', bg: '#FFF3E0', color: '#E65100', icon: <Star sx={{ fontSize: 12 }} /> },
    friends: { label: 'Friends', bg: '#E8F5F3', color: '#2D6E6A', icon: <People sx={{ fontSize: 12 }} /> },
    private: { label: 'Private', bg: '#FFEBEE', color: '#f44336', icon: <Lock sx={{ fontSize: 12 }} /> },
  };
  const order = ['public', 'bffs', 'friends', 'private'];
  const cycle = (i: number) => setPhotos((ps: any) => ps.map((p: any, j: number) => j === i ? { ...p, vis: order[(order.indexOf(p.vis) + 1) % order.length] } : p));
  return (
    <Box>
      <Narration text="Each photo has its own privacy level — tap a photo's badge to cycle public → BFFs → friends → private. Public photos appear in Community." />
      <Box sx={{ background: 'linear-gradient(135deg, #4A6FA5 0%, #3D5A8A 100%)', borderRadius: '12px 12px 0 0', color: 'white', pt: 2, pb: 2, px: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ArrowBack sx={{ color: 'white', fontSize: 20, mr: 1, ...interactiveSx }} onClick={() => go('home')} />
            <Box>
              <Typography variant="h6" fontWeight={600}>Trip Photos</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>Tap a badge to change privacy</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box sx={{ p: 2, bgcolor: '#F8FAFB', borderRadius: '0 0 12px 12px' }}>
        <Grid container spacing={1.5}>
          {photos.map((photo: any, i: number) => {
            const vs = visStyle[photo.vis];
            return (
              <Grid item xs={6} sm={4} key={i}>
                <Box sx={{ position: 'relative' }}>
                  <PhotoPlaceholder label={photo.label} src={photo.src} height={140} borderRadius={12} />
                  <Chip icon={vs.icon} label={vs.label} size="small" onClick={() => cycle(i)}
                    sx={{ position: 'absolute', bottom: 6, left: 6, bgcolor: vs.bg, color: vs.color, fontWeight: 600, fontSize: '0.65rem', height: 24, cursor: 'pointer', '& .MuiChip-icon': { color: vs.color }, '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.25)' } }} />
                </Box>
              </Grid>
            );
          })}
        </Grid>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button variant="contained" endIcon={<ArrowForward />} onClick={() => go('community', trip)}
            sx={{ bgcolor: '#2D6E6A', borderRadius: '20px', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#1F4E4A' } }}>See in Community</Button>
        </Box>
      </Box>
    </Box>
  );
}

// ─── FLOW: FRIENDS PAGE ─────────────────────────────────────────────────────
function FlowFriends({ go }: any) {
  const [friends, setFriends] = React.useState([
    { name: 'Alex Rivera', email: 'alex.rivera@email.com', status: 'friend', color: '#2D6E6A' },
    { name: 'Sam Chen', email: 'sam.chen@email.com', status: 'friend', color: '#3949ab' },
    { name: 'Jordan Taylor', email: 'jordan.taylor@email.com', status: 'friend', color: '#8e24aa' },
    { name: 'Priya Patel', email: 'priya.patel@email.com', status: 'pending', color: '#00897b' },
  ]);
  const accept = (name: string) => setFriends(prev => prev.map(f => f.name === name ? { ...f, status: 'friend' } : f));
  const decline = (name: string) => setFriends(prev => prev.filter(f => f.name !== name));
  return (
    <Box>
      <Narration text="Your travel circle. Friends can see what you share with them — and you control exactly what that is, per trip and per photo." />
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <ArrowBack sx={{ color: '#666', ...interactiveSx }} onClick={() => go('home')} />
        <People sx={{ color: '#2D6E6A' }} />
        <Typography variant="h5" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, color: '#2D6E6A', flexGrow: 1 }}>Friends</Typography>
        <Button variant="contained" size="small" disabled startIcon={<PersonAdd />} sx={{ bgcolor: '#2D6E6A', borderRadius: '24px', textTransform: 'none', fontWeight: 600, '&.Mui-disabled': { bgcolor: '#cccccc', color: '#fff' } }}>Add Friend</Button>
      </Box>
      <Card sx={{ borderRadius: 3 }}>
        {friends.map((f, i) => (
          <Box key={i}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2 }}>
              <Avatar sx={{ width: 44, height: 44, bgcolor: f.color, fontSize: '1rem' }}>{f.name[0]}</Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>{f.name}</Typography>
                <Typography variant="caption" color="text.secondary">{f.email}</Typography>
              </Box>
              {f.status === 'friend' ? (
                <Chip icon={<CheckCircle sx={{ fontSize: 14 }} />} label="Friends" size="small" sx={{ bgcolor: '#E8F5F3', color: '#2D6E6A', fontWeight: 600 }} />
              ) : (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Button size="small" variant="contained" onClick={() => accept(f.name)} sx={{ bgcolor: '#2D6E6A', textTransform: 'none', fontSize: '0.7rem', minWidth: 0, px: 1.5, '&:hover': { bgcolor: '#1F4E4A' } }}>Accept</Button>
                  <Button size="small" variant="outlined" onClick={() => decline(f.name)} sx={{ color: '#999', borderColor: '#ddd', textTransform: 'none', fontSize: '0.7rem', minWidth: 0, px: 1.5 }}>Decline</Button>
                </Box>
              )}
            </Box>
            {i < friends.length - 1 && <Divider />}
          </Box>
        ))}
      </Card>
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => go('home')} sx={{ borderColor: '#2D6E6A', color: '#2D6E6A', borderRadius: '24px', textTransform: 'none', fontWeight: 600, px: 3 }}>Back to Home</Button>
      </Box>
    </Box>
  );
}

// ─── FLOW: ANNOUNCEMENTS PANEL (dismissable) ────────────────────────────────
function FlowAnnouncements({ go }: any) {
  const [items, setItems] = React.useState([
    { id: 1, title: 'Welcome to Percoro!', body: 'Your account is set up. Start by creating or importing your first trip.', date: 'Today', icon: <Star sx={{ color: '#FFB300' }} /> },
    { id: 2, title: 'Pro plan — free during early access', body: 'You have full Pro features: 15 GB storage, trip companions, and expiration control.', date: 'Yesterday', icon: <WorkspacePremium sx={{ color: '#2D6E6A' }} /> },
    { id: 3, title: 'New: Trip Companions', body: 'Invite friends as companions so they can add photos and contribute to your trips.', date: '2 days ago', icon: <ConnectWithoutContact sx={{ color: '#3949ab' }} /> },
    { id: 4, title: 'Privacy you control', body: 'Every photo and trip has its own visibility — public, friends, BFFs, or private.', date: '3 days ago', icon: <Lock sx={{ color: '#8e24aa' }} /> },
  ]);
  const dismiss = (id: number) => setItems(prev => prev.filter(x => x.id !== id));
  return (
    <Box>
      <Narration text="Announcements keep you posted on new features and account updates. Dismiss any you've read — your unread count updates instantly." />
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <ArrowBack sx={{ color: '#666', ...interactiveSx }} onClick={() => go('home')} />
        <Notifications sx={{ color: '#2D6E6A' }} />
        <Typography variant="h5" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, color: '#2D6E6A', flexGrow: 1 }}>Announcements</Typography>
        <Chip label={items.length + ' unread'} size="small" sx={{ bgcolor: '#E8F0EE', color: '#2D6E6A', fontWeight: 600 }} />
      </Box>
      {items.length === 0 ? (
        <Card sx={{ borderRadius: 3, p: 4, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 40, color: '#2D6E6A', opacity: 0.5, mb: 1 }} />
          <Typography variant="body1" fontWeight={600}>All caught up</Typography>
          <Typography variant="body2" color="text.secondary">You've read every announcement.</Typography>
          <Button onClick={() => go('home')} sx={{ mt: 2, textTransform: 'none', color: '#2D6E6A' }}>Back to Home</Button>
        </Card>
      ) : items.map(a => (
        <Card key={a.id} variant="outlined" sx={{ borderRadius: 2, mb: 1.5 }}>
          <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 2 }}>
            <Box sx={{ bgcolor: '#F5F5F5', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{a.icon}</Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={600}>{a.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{a.body}</Typography>
              <Typography variant="caption" color="text.secondary">{a.date}</Typography>
            </Box>
            <IconButton size="small" onClick={() => dismiss(a.id)}><Close fontSize="small" /></IconButton>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
}

// ─── FLOW: PLANS PAGE ───────────────────────────────────────────────────────
function FlowPlans({ go }: any) {
  const plans = [
    { name: 'Free', price: '$0', period: 'forever', features: ['3 trips', '2 GB photo storage', 'Friends sharing', 'Community access'], current: false },
    { name: 'Pro', price: '$8', period: 'per month', features: ['Unlimited trips', '15 GB photo storage', 'Trip companions', 'Access expiration control', 'BFFs visibility tier', 'Priority support'], current: true },
  ];
  return (
    <Box>
      <Narration text="Simple plans. You're on Pro during early access — every premium feature unlocked, no charge." />
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <ArrowBack sx={{ color: '#666', ...interactiveSx }} onClick={() => go('home')} />
        <WorkspacePremium sx={{ color: '#2D6E6A' }} />
        <Typography variant="h5" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, color: '#2D6E6A' }}>Plans</Typography>
      </Box>
      <Grid container spacing={2}>
        {plans.map((p, i) => (
          <Grid item xs={12} sm={6} key={i}>
            <Card sx={{ borderRadius: 3, height: '100%', border: p.current ? '2px solid #2D6E6A' : '1px solid #e0e0e0', position: 'relative', boxShadow: p.current ? '0 6px 24px rgba(45,110,106,0.15)' : 'none' }}>
              {p.current && <Chip label="Your plan" size="small" sx={{ position: 'absolute', top: 12, right: 12, bgcolor: '#2D6E6A', color: 'white', fontWeight: 600 }} />}
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={700} sx={{ fontFamily: 'Poppins, sans-serif' }}>{p.name}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, my: 1 }}>
                  <Typography variant="h4" fontWeight={700} color="#2D6E6A">{p.price}</Typography>
                  <Typography variant="body2" color="text.secondary">/ {p.period}</Typography>
                </Box>
                <Divider sx={{ my: 1.5 }} />
                {p.features.map((f, j) => (
                  <Box key={j} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CheckCircle sx={{ fontSize: 18, color: '#2D6E6A' }} />
                    <Typography variant="body2">{f}</Typography>
                  </Box>
                ))}
                <Button fullWidth variant={p.current ? 'outlined' : 'contained'} disabled={p.current}
                  sx={{ mt: 2, borderRadius: 2, textTransform: 'none', fontWeight: 600, ...(p.current ? { borderColor: '#2D6E6A', color: '#2D6E6A' } : { bgcolor: '#2D6E6A', '&:hover': { bgcolor: '#1F4E4A' } }) }}>
                  {p.current ? 'Current Plan' : 'Upgrade to Pro'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

// ─── FLOW: MY PHOTOS (aligns with Community; public photos flow there) ───────
function FlowMyPhotos({ go, photos }: any) {
  const visStyle: any = {
    public: { label: 'Public', bg: '#E8F5E9', color: '#4CAF50', icon: <Public sx={{ fontSize: 12 }} /> },
    bffs: { label: 'BFFs', bg: '#FFF3E0', color: '#E65100', icon: <Star sx={{ fontSize: 12 }} /> },
    friends: { label: 'Friends', bg: '#E8F5F3', color: '#2D6E6A', icon: <People sx={{ fontSize: 12 }} /> },
    private: { label: 'Private', bg: '#FFEBEE', color: '#f44336', icon: <Lock sx={{ fontSize: 12 }} /> },
  };
  const publicCount = photos.filter((p: any) => p.vis === 'public').length;
  return (
    <Box>
      <Narration text="My Photos is your whole gallery across trips. Public photos appear in Community; everything else stays as private as you set it." />
      <Box sx={{ background: 'linear-gradient(135deg, #4A6FA5 0%, #3D5A8A 100%)', borderRadius: '12px 12px 0 0', color: 'white', pt: 2, pb: 2, px: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ArrowBack sx={{ color: 'white', fontSize: 20, mr: 1, ...interactiveSx }} onClick={() => go('home')} />
            <Box>
              <Typography variant="h6" fontWeight={600}>My Photos</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>{photos.length} photos · {publicCount} public in Community</Typography>
            </Box>
          </Box>
          <Button variant="contained" size="small" disabled startIcon={<CloudUpload />}
            sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.5)', borderRadius: '20px', textTransform: 'none', '&.Mui-disabled': { color: 'rgba(255,255,255,0.5)' } }}>Upload</Button>
        </Box>
      </Box>
      <Box sx={{ p: 2, bgcolor: '#F8FAFB', borderRadius: '0 0 12px 12px' }}>
        <Grid container spacing={1.5}>
          {photos.map((photo: any, i: number) => {
            const vs = visStyle[photo.vis];
            return (
              <Grid item xs={6} sm={4} key={i}>
                <Box sx={{ position: 'relative' }}>
                  <PhotoPlaceholder label={photo.label} src={photo.src} height={140} borderRadius={12} />
                  <Chip icon={vs.icon} label={vs.label} size="small" sx={{ position: 'absolute', bottom: 6, left: 6, bgcolor: vs.bg, color: vs.color, fontWeight: 600, fontSize: '0.65rem', height: 24, '& .MuiChip-icon': { color: vs.color } }} />
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Box>
  );
}

// ─── FLOW: COMMUNITY (Discover/Friends tabs, reads shared photo state) ───────
function FlowCommunity({ go, photos }: any) {
  const [tab, setTab] = React.useState(0); // 0 = Discover, 1 = Friends
  const [idx, setIdx] = React.useState(0);
  // Jordan's Cancun photos shared into Community.
  const cancun = [
    { label: 'Playa del Carmen sunrise', src: '/demo/cancun-1.jpg', vis: 'friends', owner: 'Jordan Taylor', place: 'Cancun, Mexico' },
    { label: 'Cenote swim, Yucatan', src: '/demo/cancun-2.jpg', vis: 'public', owner: 'Jordan Taylor', place: 'Cancun, Mexico' },
    { label: 'Tulum ruins at golden hour', src: '/demo/cancun-3.jpg', vis: 'public', owner: 'Jordan Taylor', place: 'Cancun, Mexico' },
  ];
  // Discover = everyone's public photos (Maya's public safari + Jordan's public Cancun).
  const mayaPublic = photos.filter((p: any) => p.vis === 'public').map((p: any) => ({ ...p, place: 'East Africa' }));
  const jordanPublic = cancun.filter(p => p.vis === 'public');
  const discover = [...mayaPublic, ...jordanPublic];
  // Friends = photos friends shared with you (Jordan's friends-visibility Cancun).
  const friendsFeed = cancun.filter(p => p.vis === 'friends');
  const feed = tab === 0 ? discover : friendsFeed;
  const safeIdx = feed.length ? idx % feed.length : 0;
  const current = feed[safeIdx];
  const switchTab = (t: number) => { setTab(t); setIdx(0); };
  const next = () => { if (feed.length) setIdx((safeIdx + 1) % feed.length); };
  return (
    <Box>
      <Narration text="Community is your social space. Discover shows everyone's public trips; Friends shows what your circle shared with you. Tap a photo to see the next." />
      <Box sx={{ borderRadius: 3, overflow: 'hidden', position: 'relative', bgcolor: '#000', minHeight: 520 }}>
        {current ? (
          <Box onClick={next} sx={{ cursor: 'pointer', position: 'relative' }}>
            <PhotoPlaceholder label={current.label} src={current.src} height={560} borderRadius={0} />
            <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.45) 0%, transparent 28%, transparent 55%, rgba(0,0,0,0.75) 100%)' }} />
            {/* Tabs */}
            <Box sx={{ position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', display: 'flex', bgcolor: 'rgba(0,0,0,0.5)', borderRadius: '30px', p: 0.5 }}>
              <Box onClick={(e: any) => { e.stopPropagation(); switchTab(0); }} sx={{ px: 2.5, py: 0.5, borderRadius: '20px', bgcolor: tab === 0 ? 'rgba(255,255,255,0.25)' : 'transparent', color: tab === 0 ? 'white' : 'rgba(255,255,255,0.7)', fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}>
                <Public sx={{ fontSize: 16 }} /> Discover
              </Box>
              <Box onClick={(e: any) => { e.stopPropagation(); switchTab(1); }} sx={{ px: 2.5, py: 0.5, borderRadius: '20px', bgcolor: tab === 1 ? 'rgba(255,255,255,0.25)' : 'transparent', color: tab === 1 ? 'white' : 'rgba(255,255,255,0.7)', fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}>
                <People sx={{ fontSize: 16 }} /> Friends
              </Box>
            </Box>
            {/* Progress dots */}
            <Box sx={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {feed.map((_: any, i: number) => (
                <Box key={i} sx={{ width: 4, height: i === safeIdx ? 24 : 12, borderRadius: 2, bgcolor: i === safeIdx ? '#2D6E6A' : 'rgba(255,255,255,0.4)' }} />
              ))}
            </Box>
            {/* Owner + caption */}
            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Avatar sx={{ width: 36, height: 36, border: '2px solid white', bgcolor: current.owner === 'Jordan Taylor' ? '#8e24aa' : '#2D6E6A', fontSize: '0.85rem' }}>{current.owner[0]}</Avatar>
                <Box>
                  <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 600, textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>{current.owner}</Typography>
                  <Chip icon={tab === 1 ? <People sx={{ fontSize: 12, color: '#2D6E6A !important' }} /> : <Public sx={{ fontSize: 12, color: '#2D6E6A !important' }} />} label={tab === 1 ? 'Friend' : 'Public'} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.9)', color: '#2D6E6A', fontWeight: 600, fontSize: '0.65rem', height: 20 }} />
                </Box>
              </Box>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, mb: 0.5, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>{current.label}</Typography>
              {current.place && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationOn sx={{ color: 'white', fontSize: 18 }} />
                  <Typography variant="body2" sx={{ color: 'white', fontWeight: 500, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>{current.place}</Typography>
                </Box>
              )}
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', display: 'block', mt: 1 }}>Tap photo for next ({safeIdx + 1} / {feed.length})</Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ p: 6, textAlign: 'center', color: 'rgba(255,255,255,0.8)' }}>
            <Box sx={{ position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', display: 'flex', bgcolor: 'rgba(0,0,0,0.5)', borderRadius: '30px', p: 0.5 }}>
              <Box onClick={() => switchTab(0)} sx={{ px: 2.5, py: 0.5, borderRadius: '20px', bgcolor: tab === 0 ? 'rgba(255,255,255,0.25)' : 'transparent', color: 'white', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>Discover</Box>
              <Box onClick={() => switchTab(1)} sx={{ px: 2.5, py: 0.5, borderRadius: '20px', bgcolor: tab === 1 ? 'rgba(255,255,255,0.25)' : 'transparent', color: 'white', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>Friends</Box>
            </Box>
            <Typography sx={{ mt: 6 }}>No {tab === 0 ? 'public' : 'friend'} photos yet. {tab === 0 ? 'Set a photo to Public on Trip Photos.' : ''}</Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Button variant="contained" onClick={() => go('home')} sx={{ bgcolor: '#2D6E6A', borderRadius: '24px', textTransform: 'none', fontWeight: 600, px: 4, '&:hover': { bgcolor: '#1F4E4A' } }}>Back to Home</Button>
      </Box>
    </Box>
  );
}

// ─── USER FLOW CONTROLLER ───────────────────────────────────────────────────
const USER_FLOW_ORDER = ['why', 'signup', 'home', 'mytrips', 'tripDetail', 'createTrip', 'share', 'uploading', 'photos', 'community'];

function UserFlow({ onExit }: any) {
  const [screen, setScreen] = React.useState('why');
  const [activeTrip, setActiveTrip] = React.useState<any>(DEMO_TRIPS[0]);
  // Shared photo privacy state — Trip Photos edits it; My Photos + Community read it live.
  const [photos, setPhotos] = React.useState([
    { label: 'Lioness at dawn, Maasai Mara', src: '/demo/ea-1.jpg', vis: 'public', owner: 'Maya Johnson' },
    { label: 'Wildebeest crossing the Mara River', src: '/demo/ea-2.jpg', vis: 'public', owner: 'Maya Johnson' },
    { label: 'Hot air balloon over the Serengeti', src: '/demo/ea-3.jpg', vis: 'bffs', owner: 'Maya Johnson' },
    { label: 'Elephant herd at a watering hole', src: '/demo/ea-4.jpg', vis: 'friends', owner: 'Maya Johnson' },
    { label: 'Acacia silhouette at sunset', src: '/demo/ea-5.jpg', vis: 'private', owner: 'Maya Johnson' },
  ]);
  const [history, setHistory] = React.useState<Array<{ screen: string; trip: any }>>([]);
  const go = (next: string, trip?: any) => {
    setHistory(h => [...h, { screen, trip: activeTrip }]);
    if (trip) setActiveTrip(trip);
    setScreen(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const back = () => {
    setHistory(h => {
      if (h.length === 0) { onExit(); return h; }
      const prev = h[h.length - 1];
      setScreen(prev.screen);
      setActiveTrip(prev.trip);
      return h.slice(0, -1);
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const labels: any = { why: 'Why Percoro', signup: 'Sign Up', home: 'Home', mytrips: 'My Trips', tripDetail: activeTrip?.title || 'Trip', createTrip: 'Create Trip', share: 'Share', uploading: 'Uploading', photos: 'Photos', community: 'Community', friends: 'Friends', announcements: 'Announcements', plans: 'Plans', myphotos: 'My Photos' };

  let body = null;
  const WhyScreen: any = UserStep_WhyPercoro;
  const CommunityScreen: any = UserStep_CommunityFriends;
  if (screen === 'why') body = <Box><WhyScreen /><Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}><Button variant="contained" endIcon={<ArrowForward />} onClick={() => go('signup')} sx={{ bgcolor: '#2D6E6A', borderRadius: '24px', textTransform: 'none', fontWeight: 600, px: 4, py: 1.2, '&:hover': { bgcolor: '#1F4E4A' } }}>Get Started</Button></Box></Box>;
  else if (screen === 'signup') body = <FlowSignup onNext={() => go('home')} />;
  else if (screen === 'home') body = <FlowHome go={go} />;
  else if (screen === 'mytrips') body = <FlowMyTrips go={go} />;
  else if (screen === 'tripDetail') body = <FlowTripDetail trip={activeTrip} go={go} />;
  else if (screen === 'createTrip') body = <FlowCreateTrip go={go} />;
  else if (screen === 'share') body = <FlowShare trip={activeTrip} go={go} />;
  else if (screen === 'uploading') body = <FlowUploading trip={activeTrip} go={go} />;
  else if (screen === 'photos') body = <FlowPhotos trip={activeTrip} go={go} photos={photos} setPhotos={setPhotos} />;
  else if (screen === 'friends') body = <FlowFriends go={go} />;
  else if (screen === 'announcements') body = <FlowAnnouncements go={go} />;
  else if (screen === 'plans') body = <FlowPlans go={go} />;
  else if (screen === 'myphotos') body = <FlowMyPhotos go={go} photos={photos} />;
  else if (screen === 'community') body = <FlowCommunity go={go} photos={photos} />;
  else if (screen === '__community_old') body = <Box><CommunityScreen /><Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}><Button variant="contained" onClick={onExit} sx={{ bgcolor: '#2D6E6A', borderRadius: '24px', textTransform: 'none', fontWeight: 600, px: 4, py: 1.2, '&:hover': { bgcolor: '#1F4E4A' } }}>Finish Demo</Button></Box></Box>;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFB' }}>
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0', px: 2, py: 1.5, position: 'sticky', top: 0, zIndex: 10 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: 900, mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={back}><ArrowBack fontSize="small" /></IconButton>
            <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, color: '#2D6E6A', fontSize: '1rem' }}>PERCORO</Typography>
            <Chip label="User Demo" size="small" sx={{ bgcolor: '#E8F0EE', color: '#2D6E6A', fontWeight: 600 }} />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>{labels[screen]}</Typography>
          </Box>
          <Button size="small" onClick={onExit} sx={{ textTransform: 'none', color: 'text.secondary' }}>Exit Demo</Button>
        </Box>
      </Box>
      <Box sx={{ maxWidth: 700, mx: 'auto', p: 3, pb: 10 }}>{body}</Box>
    </Box>
  );
}

// ─── MAIN DEMO PAGE ─────────────────────────────────────────────────────────

// ─── ADMIN FLOW (interactive state machine — replaces the 9-dot Admin stepper) ──
// Sidebar destinations: demo-active items navigate; the rest are greyed/disabled.
const ADMIN_SIDEBAR = [
  { key: 'home',     label: 'Admin Home',      group: '' },
  { key: 'support',  label: 'User Support',    group: 'Operations' },
  { key: 'health',   label: 'System Health',   group: 'Operations' },
  { key: 'access',  label: 'Early Access',    group: 'Operations' },
  { key: 'ai',       label: 'AI Operations',   group: 'Operations' },
  { key: 'notify',   label: 'Notifications',   group: 'Product' },
  { key: 'resources',label: 'Resources',       group: 'Product' },
  { key: 'flags',    label: 'Feature Flags',   group: 'Platform' },
];
// Real admin sections shown for breadth but disabled in the demo.
const ADMIN_SIDEBAR_DISABLED = [
  { label: 'Users',            group: 'Operations' },
  { label: 'Plan Requests',    group: 'Operations' },
  { label: 'Subscribers',      group: 'Product' },
  { label: 'Analytics',        group: 'Business' },
  { label: 'Cost Analytics',   group: 'Business' },
  { label: 'Audit & Compliance', group: 'Platform' },
  { label: 'Staff & RBAC',     group: 'Platform' },
  { label: 'Legal Documents',  group: 'Platform' },
  { label: 'System Management', group: 'Platform' },
];

function AdminSidebar({ active, go }: any) {
  const groups = ['', 'Operations', 'Product', 'Business', 'Platform'];
  const groupLabel: any = { '': '', Operations: 'Operations', Product: 'Product & Marketing', Business: 'Business', Platform: 'Platform' };
  return (
    <Box sx={{ width: 210, flexShrink: 0, borderRight: '1px solid #eee', pr: 1, display: { xs: 'none', md: 'block' } }}>
      {groups.map((g) => {
        const active_items = ADMIN_SIDEBAR.filter((s) => s.group === g);
        const disabled_items = ADMIN_SIDEBAR_DISABLED.filter((s) => s.group === g);
        if (active_items.length === 0 && disabled_items.length === 0) return null;
        return (
          <Box key={g || 'home'} sx={{ mb: 1 }}>
            {g && <Typography variant="caption" sx={{ px: 1.5, color: '#9aa', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.62rem', letterSpacing: 0.5 }}>{groupLabel[g]}</Typography>}
            <List dense disablePadding>
              {active_items.map((s) => (
                <ListItem key={s.key} disablePadding>
                  <Box onClick={() => go(s.key)} sx={{ width: '100%', px: 1.5, py: 0.6, borderRadius: 1.5, cursor: 'pointer', bgcolor: active === s.key ? '#E8F0EE' : 'transparent', '&:hover': { bgcolor: active === s.key ? '#E8F0EE' : '#F3F6F5' } }}>
                    <Typography variant="body2" sx={{ fontWeight: active === s.key ? 700 : 500, color: active === s.key ? '#2D6E6A' : '#333', fontSize: '0.83rem' }}>{s.label}</Typography>
                  </Box>
                </ListItem>
              ))}
              {disabled_items.map((s, i) => (
                <ListItem key={'d' + i} disablePadding>
                  <Box sx={{ width: '100%', px: 1.5, py: 0.6, borderRadius: 1.5, cursor: 'not-allowed' }}>
                    <Typography variant="body2" sx={{ color: '#c4c4c4', fontSize: '0.83rem' }}>{s.label}</Typography>
                  </Box>
                </ListItem>
              ))}
            </List>
          </Box>
        );
      })}
    </Box>
  );
}

// Shared chrome: top bar + (sidebar + main). Back-to-home from any non-home screen.
function AdminShell({ active, go, onExit, children }: any) {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFB' }}>
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0', px: 2, py: 1.5, position: 'sticky', top: 0, zIndex: 10 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: 1100, mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {active !== 'signin' && active !== 'home' && (
              <IconButton size="small" onClick={() => go('home')}><ArrowBack fontSize="small" /></IconButton>
            )}
            <Typography component="a" href="/" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, color: '#2D6E6A', fontSize: '1rem', textDecoration: 'none', cursor: 'pointer' }}>PERCORO</Typography>
            <Chip label="Admin Demo" size="small" sx={{ bgcolor: '#E8EEF0', color: '#3949ab', fontWeight: 600 }} />
          </Box>
          <Button size="small" onClick={onExit} sx={{ textTransform: 'none', color: 'text.secondary' }}>Exit Demo</Button>
        </Box>
      </Box>
      <Box sx={{ maxWidth: 1100, mx: 'auto', p: 3, display: 'flex', gap: 2 }}>
        {active !== 'signin' && <AdminSidebar active={active} go={go} />}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>{children}</Box>
      </Box>
    </Box>
  );
}

// Small reusable "back to home" button for the bottom of each drill-in screen.
function BackToHome({ go }: any) {
  return (
    <Button onClick={() => go('home')} startIcon={<ArrowBack />} sx={{ mt: 3, textTransform: 'none', color: '#2D6E6A' }}>
      Back to Admin Home
    </Button>
  );
}

// ── ADMIN: SIGN IN (mirrors the user sign-in pattern; description kept at bottom) ──
function AdminSignIn({ go }: any) {
  const email = useAutoFill('alex.morgan@percoro.com');
  const pwd = useAutoFill('password123');
  return (
    <Box>
      <Narration text="Admin login — role-based access control. Super admins and admins redirect to the admin dashboard. Every admin action is audit-logged with IP, timestamp, and reason." />
      <Card sx={{ maxWidth: 420, mx: 'auto', borderRadius: 4, overflow: 'hidden' }}>
        <Box sx={{ bgcolor: '#2D6E6A', px: 3, py: 2.5, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '1.3rem', color: 'white' }}>PERCORO</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem' }}>Admin Console</Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          <TextField fullWidth size="small" label="Email" value={email.val} onClick={email.animate}
            InputProps={{ readOnly: true }} sx={{ mb: 2, ...interactiveSx }} />
          <TextField fullWidth size="small" label="Password" type="password" value={pwd.val} onClick={pwd.animate}
            InputProps={{ readOnly: true }} sx={{ mb: 2, ...interactiveSx }} />
          <Button fullWidth variant="contained" onClick={() => go('home')}
            sx={{ bgcolor: '#2D6E6A', borderRadius: '24px', textTransform: 'none', fontWeight: 700, py: 1.2, mb: 2, '&:hover': { bgcolor: '#1F4E4A' } }}>
            Sign In
          </Button>
          <Box sx={{ bgcolor: '#E8F5F3', borderRadius: 2, p: 2, textAlign: 'center' }}>
            <CheckCircle sx={{ color: '#4CAF50', fontSize: 20, mr: 0.5, verticalAlign: 'middle' }} />
            <Typography variant="body2" sx={{ display: 'inline', fontWeight: 600, color: '#2D6E6A' }}>
              Role: super_admin — redirecting to dashboard
            </Typography>
          </Box>
          <Box sx={{ mt: 2, p: 1.5, bgcolor: '#F5F5F5', borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Role-based access:</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>super_admin — full dashboard access</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>admin — read-only + support</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>user — redirected to home</Typography>
          </Box>
        </Box>
      </Card>
    </Box>
  );
}

// ── ADMIN: HOME (clickable attention cards, auto-fit value font) ──
function AdminHome({ go, accessCount }: any) {
  const cards = [
    { key: 'support', title: 'User Support', icon: <Support sx={{ color: '#2D6E6A' }} />, value: '3', caption: '1 SLA-breached · 2 urgent', tone: 'error' },
    { key: 'health',  title: 'System Health', icon: <Settings sx={{ color: '#2D6E6A' }} />, value: 'Operational', caption: 'All checks passing', tone: 'calm' },
    { key: 'access',  title: 'Early Access', icon: <PersonAdd sx={{ color: '#2D6E6A' }} />, value: String(accessCount), caption: accessCount > 0 ? accessCount + ' pending review' : 'All cleared', tone: accessCount > 0 ? 'attention' : 'calm' },
    { key: 'ai',      title: 'AI Warnings', icon: <AutoAwesome sx={{ color: '#2D6E6A' }} />, value: '1.6%', caption: 'Error rate nominal', tone: 'calm' },
  ];
  const toneColor = (t: string) => (t === 'error' ? '#C62828' : t === 'attention' ? '#B26A00' : '#2D6E6A');
  return (
    <Box>
      <Narration text="Admins land on a live snapshot of what needs attention — open support tickets, system health, pending access requests, and AI reliability. Click any card to drill in." />
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>Admin Home Page</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>A live snapshot across support, system, access, and AI. Every card is clickable.</Typography>
      <Grid container spacing={2}>
        {cards.map((c) => {
          const color = toneColor(c.tone);
          const flagged = c.tone !== 'calm';
          const longValue = c.value.length > 6;
          return (
            <Grid item xs={12} sm={6} md={3} key={c.key}>
              <Card variant="outlined" onClick={() => go(c.key)}
                sx={{ borderRadius: 3, height: '100%', cursor: 'pointer', transition: 'all 0.15s', borderColor: flagged ? color : '#E0ECEA', borderWidth: flagged ? 2 : 1, '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 6px 20px rgba(0,0,0,0.1)' } }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ bgcolor: '#E8F0EE', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{c.icon}</Box>
                    {flagged && <Chip size="small" label="Attention" sx={{ bgcolor: c.tone === 'error' ? '#FBE3E3' : '#FFF3E0', color, fontWeight: 600, height: 22 }} />}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{c.title}</Typography>
                  <Typography fontWeight={700} sx={{ color, lineHeight: 1.15, my: 0.25, fontSize: longValue ? '1.35rem' : '2.125rem', whiteSpace: 'nowrap' }}>{c.value}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{c.caption}</Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, fontStyle: 'italic' }}>Tip: use the cards above or the sidebar to navigate. Greyed sidebar items are real sections, disabled in this demo.</Typography>
    </Box>
  );
}

// ── ADMIN: USER SUPPORT (3 tickets, each with a clickable action plan) ──
const ADMIN_TICKETS = [
  {
    id: 'PCR-4821', subject: "Can't upload booking PDF — import fails", user: 'Sam Chen', tier: 'free',
    priority: 'SLA-breached', age: '26h open', status: 'open',
    plan: [
      'Confirm the file is a text-based PDF (not a scanned image) — import parses embedded text.',
      'Check the AI trip-import invocation log for this user (one error logged Jun 1, 4:22 PM).',
      'If the parser timed out, retry import; if it fails again, escalate to engineering with the file.',
    ],
  },
  {
    id: 'PCR-4830', subject: 'Friend invite accepted but not showing in Friends', user: 'Priya Patel', tier: 'free',
    priority: 'urgent', age: '5h open', status: 'open',
    plan: [
      'Check user_friends for a duplicate/stale row (known issue #337 — accept can create a duplicate).',
      'Verify the inviter cancelled-then-resent (cancel may leave a stale row, #335).',
      'Resolve to a single accepted row; confirm the connection appears for both users.',
    ],
  },
  {
    id: 'PCR-4835', subject: 'Password reset email not received', user: 'Alex Rivera', tier: 'free',
    priority: 'urgent', age: '2h open', status: 'open',
    plan: [
      'Confirm the account exists and is confirmed (not a zombie unconfirmed account, #336).',
      'Check the cognito-email-sender CloudWatch logs for a send event to this address.',
      'If sent, advise checking spam; if not sent, re-trigger reset and watch the log.',
    ],
  },
];

function AdminSupport({ go }: any) {
  const [subTab, setSubTab] = useState(0);
  const baseTickets = [
    { id: 'PCR-4821', subject: "Can't upload booking PDF — import fails", cat: 'bug', user: 'Sam Chen', email: 'sam.chen@example.com', priority: 'urgent', status: 'open', sla: '5h to breach', messages: 2, created: 'Jun 1, 2026' },
    { id: 'PCR-4830', subject: 'Friend invite accepted but not in Friends', cat: 'bug', user: 'Priya Patel', email: 'priya.patel@example.com', priority: 'high', status: 'open', sla: '11h to breach', messages: 1, created: 'Jun 1, 2026' },
    { id: 'PCR-4835', subject: 'Password reset email not received', cat: 'account', user: 'Alex Rivera', email: 'alex.rivera@example.com', priority: 'high', status: 'pending', sla: 'On track', messages: 3, created: 'Jun 2, 2026' },
    { id: 'PCR-3902', subject: 'Photo upload not displaying', cat: 'bug', user: 'Jordan Blake', email: 'jordan.blake@example.com', priority: 'high', status: 'closed', sla: '5/29/2026, 1:01:32 AM', messages: 1, created: '5/28/2026' },
    { id: 'PCR-3710', subject: 'Login screen not loading', cat: 'bug', user: 'Riley Nguyen', email: 'riley.nguyen@example.com', priority: 'normal', status: 'closed', sla: '5/15/2026, 6:26:06 PM', messages: 1, created: '5/14/2026' },
  ];
  // Lifted per-ticket state: status + thread, seeded once, persists for the session.
  const [ticketState, setTicketState] = useState<any>(() => {
    const init: any = {};
    baseTickets.forEach((t) => {
      init[t.id] = {
        status: t.status,
        priority: t.priority,
        thread: [{ author: t.user, role: 'user', when: t.created, body: t.subject + '. Please take a look when you can.', internal: false }],
      };
    });
    return init;
  });
  const [openId, setOpenId] = useState<string | null>(null);
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [replyText, setReplyText] = useState('');

  const CANNED = [
    { title: 'Acknowledge — investigating', body: "Thanks for reaching out. I've reproduced the issue and am investigating now. I'll update you within one business day." },
    { title: 'Need more info', body: 'Could you share the file you were trying to upload and the email on your account? That will help me trace what is happening.' },
    { title: 'Resolved — please confirm', body: 'This should be fixed now. Please try again and let me know if you still run into the problem.' },
    { title: 'Escalated to engineering', body: "I've escalated this to our engineering team with the details. I'll follow up as soon as I have an update." },
  ];

  const priorityColor = (p: string) => p === 'urgent' ? 'error' : p === 'high' ? 'warning' : 'default';
  const statusColor = (s: string) => s === 'open' ? 'info' : s === 'pending' ? 'warning' : s === 'resolved' ? 'success' : 'default';
  const FilterBox = ({ label }: { label: string }) => (
    <Box sx={{ minWidth: 150, px: 1.5, py: 1, border: '1px solid #c4c4c4', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#666', fontSize: '0.9rem' }}>
      <span>{label}</span><span style={{ fontSize: '0.7rem' }}>{String.fromCharCode(9662)}</span>
    </Box>
  );

  const open = openId ? { base: baseTickets.find((t) => t.id === openId), st: ticketState[openId] } : null;

  const setStatus = (val: string) => {
    if (!openId) return;
    setTicketState((prev: any) => ({ ...prev, [openId]: { ...prev[openId], status: val } }));
  };
  const setPriority = (val: string) => {
    if (!openId) return;
    setTicketState((prev: any) => ({ ...prev, [openId]: { ...prev[openId], priority: val } }));
  };
  const sendReply = () => {
    if (!openId || !replyText.trim()) return;
    setTicketState((prev: any) => ({
      ...prev,
      [openId]: { ...prev[openId], thread: [...prev[openId].thread, { author: 'You', role: 'admin', when: 'Just now', body: replyText.trim(), internal: isInternalNote }] },
    }));
    setReplyText('');
  };

  return (
    <Box>
      <Narration text="User Support — the live ticket queue. Filter by status, priority, or category; click a ticket to open it, change status, and reply. Pick a canned response or write your own; switch to Internal Note to log a defect or feature change for the team. Contact Inquiries, Canned Responses, and an SLA Dashboard live alongside it." />
      <Typography variant="h5" fontWeight={600} gutterBottom>Support</Typography>
      <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Queue" icon={<ConfirmationNumber />} iconPosition="start" />
        <Tab label="Contact Inquiries" icon={<Mail />} iconPosition="start" disabled />
        <Tab label="Canned Responses" icon={<Reply />} iconPosition="start" disabled />
        <Tab label="SLA Dashboard" icon={<Schedule />} iconPosition="start" disabled />
      </Tabs>

      {subTab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FilterBox label="Status" />
            <FilterBox label="Priority" />
            <FilterBox label="Category" />
            <Button variant="outlined" size="small" startIcon={<Refresh />} sx={{ textTransform: 'none', borderColor: '#c4c4c4', color: '#444' }}>Refresh</Button>
          </Box>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead><TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Subject</strong></TableCell>
                <TableCell><strong>User</strong></TableCell>
                <TableCell><strong>Priority</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>SLA</strong></TableCell>
                <TableCell><strong>Messages</strong></TableCell>
                <TableCell><strong>Created</strong></TableCell>
              </TableRow></TableHead>
              <TableBody>
                {baseTickets.map((t) => {
                  const st = ticketState[t.id];
                  return (
                    <TableRow key={t.id} hover sx={{ cursor: 'pointer' }} onClick={() => { setOpenId(t.id); setIsInternalNote(false); setReplyText(''); }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{t.subject}</Typography>
                        <Typography variant="caption" color="text.secondary">{t.cat}</Typography>
                      </TableCell>
                      <TableCell><Typography variant="body2">{t.user}</Typography></TableCell>
                      <TableCell><Chip label={st.priority} color={priorityColor(st.priority) as any} size="small" /></TableCell>
                      <TableCell><Chip label={st.status} color={statusColor(st.status) as any} size="small" /></TableCell>
                      <TableCell><Typography variant="caption">{t.sla}</Typography></TableCell>
                      <TableCell>{st.thread.length}</TableCell>
                      <TableCell><Typography variant="caption">{t.created}</Typography></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
      {subTab === 1 && <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}><Typography variant="body2">Contact inquiries from the public contact form appear here.</Typography></Box>}
      {subTab === 2 && <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}><Typography variant="body2">Canned responses — reusable reply macros to speed up support.</Typography></Box>}
      {subTab === 3 && <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}><Typography variant="body2">SLA Dashboard — response/resolution targets and breach tracking.</Typography></Box>}

      <Drawer anchor="right" open={!!open} onClose={() => setOpenId(null)} PaperProps={{ sx: { width: { xs: '100%', sm: 460 }, p: 3 } }}>
        {open && open.base && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>{open.base.subject}</Typography>
              <IconButton onClick={() => setOpenId(null)}><Close /></IconButton>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip label={open.st.priority} color={priorityColor(open.st.priority) as any} size="small" />
              <Chip label={open.st.status} color={statusColor(open.st.status) as any} size="small" />
              {open.st.priority === 'urgent' && open.st.status !== 'closed' && open.st.status !== 'resolved' && <Chip label="SLA BREACHED" color="error" size="small" icon={<Warning />} />}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              From: <strong>{open.base.user}</strong> ({open.base.email})
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              SLA target: {open.base.sla}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select value={open.st.status} label="Status" onChange={(e) => setStatus(e.target.value)}>
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Priority</InputLabel>
                <Select value={open.st.priority} label="Priority" onChange={(e) => setPriority(e.target.value)}>
                  <MenuItem value="urgent">Urgent</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Thread</Typography>
            <Box sx={{ maxHeight: 260, overflowY: 'auto', mb: 2 }}>
              {open.st.thread.map((m: any, i: number) => (
                <Box key={i} sx={{ mb: 1.5, p: 1.5, borderRadius: 1, backgroundColor: m.internal ? '#fff8e1' : m.role === 'user' ? '#f5f5f5' : '#e8f5e9', border: m.internal ? '1px dashed #f9a825' : '1px solid transparent' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" fontWeight={600}>{m.author} ({m.role}){m.internal && ' — Internal Note'}</Typography>
                    <Typography variant="caption" color="text.secondary">{m.when}</Typography>
                  </Box>
                  <Typography variant="body2">{m.body}</Typography>
                </Box>
              ))}
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Reply</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
              <Button size="small" variant={!isInternalNote ? 'contained' : 'outlined'} onClick={() => setIsInternalNote(false)} sx={{ backgroundColor: !isInternalNote ? '#2D6E6A' : undefined, textTransform: 'none' }}>Public Reply</Button>
              <Button size="small" variant={isInternalNote ? 'contained' : 'outlined'} color="warning" onClick={() => setIsInternalNote(true)} sx={{ textTransform: 'none' }}>Internal Note</Button>
            </Box>
            {!isInternalNote && (
              <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
                <InputLabel>Insert canned response</InputLabel>
                <Select value="" label="Insert canned response" onChange={(e) => { const c = CANNED.find((x) => x.title === e.target.value); if (c) setReplyText(c.body); }}>
                  {CANNED.map((c) => <MenuItem key={c.title} value={c.title}>{c.title}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            <TextField multiline rows={3} fullWidth size="small"
              placeholder={isInternalNote ? 'Internal note — capture a defect or feature change (not visible to user)...' : 'Reply to user...'}
              value={replyText} onChange={(e) => setReplyText(e.target.value)} sx={{ mb: 1 }} />
            <Button variant="contained" fullWidth startIcon={<Reply />} disabled={!replyText.trim()} onClick={sendReply} sx={{ backgroundColor: isInternalNote ? '#f9a825' : '#2D6E6A', '&:hover': { backgroundColor: isInternalNote ? '#e69500' : '#1F4E4A' } }}>
              {isInternalNote ? 'Save Internal Note' : 'Send Reply'}
            </Button>
          </Box>
        )}
      </Drawer>

      <BackToHome go={go} />
    </Box>
  );
}

// ── ADMIN: SYSTEM HEALTH ──
function AdminHealth({ go }: any) {
  const [subTab, setSubTab] = useState(3);
  const perFunction = [
    { name: 'feature-flags', invocations: 14, errors: 0, errorRate: 0 },
    { name: 'support', invocations: 2, errors: 0, errorRate: 0 },
    { name: 'resources', invocations: 9, errors: 0, errorRate: 0 },
    { name: 'admin-users', invocations: 3, errors: 0, errorRate: 0 },
    { name: 'users-profile', invocations: 1, errors: 0, errorRate: 0 },
    { name: 'ai-planner', invocations: 0, errors: 0, errorRate: 0 },
  ];
  return (
    <Box>
      <Narration text="System Health — live platform health from CloudWatch over the last hour: Lambda reliability, API Gateway errors and latency, and active alarms. Overall status reflects the worst signal. It sits under System Management alongside Knowledge Base and the Audit Log." />
      <Typography variant="h5" fontWeight={600} gutterBottom>System Management</Typography>
      <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tab label="Knowledge Base" value={1} disabled />
        <Tab label="Audit Log" value={2} disabled />
        <Tab label="System Health" value={3} />
      </Tabs>

      {subTab === 1 && <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}><Typography variant="body2">Knowledge Base management — manage AI admin chat knowledge.</Typography></Box>}
      {subTab === 2 && <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}><Typography variant="body2">Audit Log — view all admin actions and system changes.</Typography></Box>}
      {subTab === 3 && (
        <Box>
          <Box sx={{ bgcolor: '#E8F5E9', border: '1px solid #A5D6A7', borderRadius: 1, p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle sx={{ color: '#2E7D32', fontSize: 20 }} />
            <Typography variant="body2" sx={{ color: '#1B5E20' }}>Platform status: <strong>OPERATIONAL</strong>  ·  checked 2:27:27 PM</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Live platform health from CloudWatch over the last hour — Lambda reliability, API Gateway errors and latency, and active alarms. Overall status reflects the worst signal.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">Lambda error rate (1h)</Typography>
              <Typography variant="h5" fontWeight={700}>0.00%</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Errors {String.fromCharCode(247)} invocations across key functions</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">API 5xx (1h)</Typography>
              <Typography variant="h5" fontWeight={700}>0</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Server (5xx) errors returned by the API gateway</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">API latency p99</Typography>
              <Typography variant="h5" fontWeight={700}>0ms</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>99th-percentile API response time</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">Alarms firing</Typography>
              <Typography variant="h5" fontWeight={700}>0</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>CloudWatch alarms currently in ALARM</Typography>
            </Paper>
          </Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Per-function (last 1h)</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Function</TableCell>
                <TableCell align="right">Invocations</TableCell>
                <TableCell align="right">Errors</TableCell>
                <TableCell align="right">Error rate</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {perFunction.map((f) => (
                  <TableRow key={f.name}>
                    <TableCell>{f.name}</TableCell>
                    <TableCell align="right">{f.invocations}</TableCell>
                    <TableCell align="right">{f.errors}</TableCell>
                    <TableCell align="right">
                      <Chip size="small" label={(f.errorRate * 100).toFixed(1) + '%'} color={f.errorRate > 0.01 ? 'error' : 'default'} variant={f.errorRate > 0.01 ? 'filled' : 'outlined'} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <BackToHome go={go} />
    </Box>
  );
}

// ── ADMIN: ACCESS REQUESTS (approve → Approved list, reject → Rejected list) ──
function AdminAccess({ go, requests, setRequests }: any) {
  const [filter, setFilter] = useState('pending');
  const decide = (id: string, verb: 'approved' | 'rejected') => {
    setRequests((prev: any[]) => prev.map((r) => r.id === id ? { ...r, status: verb } : r));
  };
  const filtered = requests.filter((r: any) => r.status === filter);
  const btnNeutral = { textTransform: 'none', borderRadius: 2, color: '#2D6E6A', borderColor: '#2D6E6A', fontSize: '0.72rem' };
  const btnDanger = { textTransform: 'none', borderRadius: 2, color: '#d32f2f', borderColor: '#d32f2f', fontSize: '0.72rem' };
  const renderActions = (req: any) => {
    if (req.status === 'pending') return (
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <Button size="small" variant="contained" startIcon={<CheckCircle sx={{ fontSize: 14 }} />} onClick={() => decide(req.id, 'approved')} sx={{ bgcolor: '#2D6E6A', textTransform: 'none', borderRadius: 2, fontSize: '0.72rem', '&:hover': { bgcolor: '#1F4E4A' } }}>Approve</Button>
        <Button size="small" variant="outlined" startIcon={<Close sx={{ fontSize: 14 }} />} onClick={() => decide(req.id, 'rejected')} sx={btnDanger}>Reject</Button>
        <Button size="small" variant="outlined" sx={btnDanger}>Delete</Button>
      </Box>
    );
    if (req.status === 'approved') return (
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <Button size="small" variant="outlined" sx={btnNeutral}>Resend</Button>
        <Button size="small" variant="outlined" sx={btnNeutral}>Reset</Button>
        <Button size="small" variant="outlined" sx={btnDanger}>Revoke</Button>
        <Button size="small" variant="outlined" sx={btnDanger}>Delete</Button>
      </Box>
    );
    return (
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <Button size="small" variant="outlined" sx={btnNeutral}>Reset</Button>
        <Button size="small" variant="outlined" sx={btnDanger}>Delete</Button>
      </Box>
    );
  };
  return (
    <Box>
      <Narration text="Early Access — admin-controlled signup. Review requests and approve or reject; approved users get an email with a signup link, and the signup Lambda enforces the gate. Approve or reject a pending row and watch it move between the Pending, Approved, and Rejected views." />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>Early Access Requests</Typography>
        <ToggleButtonGroup value={filter} exclusive onChange={(_, v) => v && setFilter(v)} size="small">
          <ToggleButton value="pending">Pending</ToggleButton>
          <ToggleButton value="approved">Approved</ToggleButton>
          <ToggleButton value="rejected">Rejected</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {filtered.length === 0 ? (
        <Card variant="outlined" sx={{ borderRadius: 2, p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">No {filter} access requests.</Typography>
        </Card>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead><TableRow sx={{ bgcolor: '#F5F5F5' }}>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {filtered.map((req: any) => (
                <TableRow key={req.id} hover>
                  <TableCell><Typography variant="body2" fontWeight={600}>{req.name}</Typography></TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{req.email}</Typography></TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{req.message || '\u2014'}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{req.date}</Typography></TableCell>
                  <TableCell align="right">{renderActions(req)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Box sx={{ mt: 2, p: 2, bgcolor: '#F0FAF9', borderRadius: 2, border: '1px solid #C8DDD8' }}>
        <Typography variant="caption" fontWeight={600} color="#2D6E6A">How it works:</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Approve {String.fromCharCode(8594)} user gets email with signup link {String.fromCharCode(8594)} signup Lambda checks approval {String.fromCharCode(8594)} account created
        </Typography>
      </Box>
      <BackToHome go={go} />
    </Box>
  );
}

// ── ADMIN: AI OPERATIONS (the AI Warnings destination) ──
function AdminAIOps({ go }: any) {
  return (
    <Box>
      <Narration text="AI Operations — real-time cost tracking, invocation logging, and per-feature spend breakdown. Every Bedrock call is logged with tokens, cost, duration, and status." />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>AI Operations</Typography>
          <Typography variant="body2" color="text.secondary">Spend, latency, and reliability across all AI-powered features</Typography>
        </Box>
        <Box sx={{ minWidth: 140, px: 1.5, py: 0.75, border: '1px solid #ccc', borderRadius: 1, fontSize: '0.85rem', color: '#333' }}>Last 7 days</Box>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <Card variant="outlined"><CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}><AttachMoney sx={{ color: '#2D6E6A' }} /><Typography variant="body2" color="text.secondary">Total Spend</Typography></Box>
          <Typography variant="h4" fontWeight={700}>$14.82</Typography>
          <Typography variant="caption" color="text.secondary">12,847 in / 4,203 out tokens</Typography>
        </CardContent></Card>
        <Card variant="outlined"><CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}><AutoAwesome sx={{ color: '#2D6E6A' }} /><Typography variant="body2" color="text.secondary">Invocations</Typography></Box>
          <Typography variant="h4" fontWeight={700}>187</Typography>
          <Typography variant="caption" color="text.secondary">182 success / 3 error</Typography>
        </CardContent></Card>
        <Card variant="outlined"><CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}><Notifications sx={{ color: '#2D6E6A' }} /><Typography variant="body2" color="text.secondary">Error Rate</Typography></Box>
          <Typography variant="h4" fontWeight={700}>1.6%</Typography>
          <Typography variant="caption" color="text.secondary">0 disabled / 2 rate-limited</Typography>
        </CardContent></Card>
        <Card variant="outlined"><CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}><Settings sx={{ color: '#2D6E6A' }} /><Typography variant="body2" color="text.secondary">Avg Latency</Typography></Box>
          <Typography variant="h4" fontWeight={700}>842ms</Typography>
          <Typography variant="caption" color="text.secondary">Successful invocations only</Typography>
        </CardContent></Card>
      </Box>
      <Card variant="outlined" sx={{ mb: 3 }}><CardContent>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>By Feature</Typography>
        <Table size="small"><TableHead><TableRow>
          <TableCell>Feature</TableCell><TableCell align="right">Invocations</TableCell><TableCell align="right">Spend</TableCell><TableCell align="right">Errors</TableCell><TableCell align="right">Avg Latency</TableCell><TableCell align="right">Last Invoked</TableCell>
        </TableRow></TableHead><TableBody>
          {[
            { f: 'ai_trip_planner', inv: 89, spend: '$7.2400', err: 1, lat: '1,204ms', last: 'Jun 2, 2:14 PM' },
            { f: 'ai_photo_analysis', inv: 64, spend: '$4.1800', err: 2, lat: '634ms', last: 'Jun 2, 1:47 PM' },
            { f: 'ai_trip_import', inv: 34, spend: '$3.4000', err: 0, lat: '921ms', last: 'Jun 1, 11:30 AM' },
          ].map((r, i) => (
            <TableRow key={i}>
              <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{r.f}</Typography></TableCell>
              <TableCell align="right">{r.inv}</TableCell>
              <TableCell align="right">{r.spend}</TableCell>
              <TableCell align="right">{r.err > 0 ? <Chip label={r.err} size="small" color="error" /> : '0'}</TableCell>
              <TableCell align="right">{r.lat}</TableCell>
              <TableCell align="right"><Typography variant="caption" color="text.secondary">{r.last}</Typography></TableCell>
            </TableRow>
          ))}
        </TableBody></Table>
      </CardContent></Card>
      <BackToHome go={go} />
    </Box>
  );
}

function AdminNotify({ go }: any) {
  const FEATURE_MSG = 'New Trip Companions is now live for Pro users — plan and share trips together, with shared itineraries and per-trip chat.';
  const [message, setMessage] = useState(FEATURE_MSG);
  const [typing, setTyping] = useState(false);
  const typeTimer = useRef<any>(null);
  const retypeMessage = () => {
    if (typeTimer.current) clearInterval(typeTimer.current);
    setTyping(true);
    setMessage('');
    let i = 0;
    typeTimer.current = setInterval(() => {
      i += 1;
      setMessage(FEATURE_MSG.slice(0, i));
      if (i >= FEATURE_MSG.length) { clearInterval(typeTimer.current); setTyping(false); }
    }, 14);
  };

  const CATEGORIES = [
    { value: 'security', label: 'Security' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'site_status', label: 'Site Status' },
    { value: 'feature_announcement', label: 'New Feature' },
    { value: 'marketing', label: 'News' },
  ];
  const catLabel = (v: string) => (CATEGORIES.find((c) => c.value === v) || { label: v }).label;
  const recipientLabel = (rt: string, role: string) => rt === 'all' ? 'All users' : 'Role: ' + role;

  const [severity, setSeverity] = useState('info');
  const [category, setCategory] = useState('feature_announcement');
  const [recipientType, setRecipientType] = useState('all');
  const [targetRole, setTargetRole] = useState('user');
  const [sending, setSending] = useState(false);

  const [active, setActive] = useState<any[]>([
    { id: 'b1', message: 'Welcome to Percoro! Start planning your first trip.', severity: 'info', category: 'feature_announcement', recipientType: 'all', role: 'user', sent: 'May 28, 2026' },
    { id: 'b2', message: 'Pro plan: free early access for our first 500 members.', severity: 'info', category: 'marketing', recipientType: 'all', role: 'user', sent: 'May 30, 2026' },
  ]);
  const [recalled, setRecalled] = useState<any[]>([]);

  const send = () => {
    if (!message.trim() || sending) return;
    setSending(true);
    setTimeout(() => {
      setActive((prev) => [{ id: 'b' + (prev.length + recalled.length + 1), message: message.trim(), severity, category, recipientType, role: targetRole, sent: 'Just now' }, ...prev]);
      setSending(false);
    }, 1100);
  };
  const recall = (b: any) => {
    setActive((prev) => prev.filter((x) => x.id !== b.id));
    setRecalled((prev) => [{ ...b, recalledAt: 'Just now' }, ...prev]);
  };

  const sevColor = (sv: string) => sv === 'warning' ? 'warning' : 'info';

  return (
    <Box>
      <Narration text="Notifications — compose a broadcast that lands in every targeted user's bell. Set severity, category, and audience, then send. Click the message to watch it retype, change any field, and send. Recalls move a broadcast into the audited Recalled list and stop it showing for users." />
      <Typography variant="h5" fontWeight={600} gutterBottom>Notifications</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Send broadcasts to users in the bell. Recalls are audited — broadcasts stop showing once recalled.</Typography>

      <Card variant="outlined" sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Campaign sx={{ color: '#2D6E6A' }} />
            <Typography variant="h6" fontWeight={600}>Compose Broadcast</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField label="Message" value={message} onChange={(e) => setMessage(e.target.value)} onClick={retypeMessage}
                fullWidth multiline minRows={3} maxRows={8} disabled={sending}
                helperText={(typing ? 'typing… ' : '') + message.length + ' / 2000 characters'} sx={interactiveSx} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth disabled={sending}>
                <InputLabel>Severity</InputLabel>
                <Select value={severity} label="Severity" onChange={(e) => setSeverity(e.target.value)}>
                  <MenuItem value="info">Info</MenuItem>
                  <MenuItem value="warning">Warning</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth disabled={sending}>
                <InputLabel>Category</InputLabel>
                <Select value={category} label="Category" onChange={(e) => setCategory(e.target.value)}>
                  {CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth disabled={sending}>
                <InputLabel>Recipient</InputLabel>
                <Select value={recipientType} label="Recipient" onChange={(e) => setRecipientType(e.target.value)}>
                  <MenuItem value="all">All users</MenuItem>
                  <MenuItem value="role">By role</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {recipientType === 'role' && (
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth disabled={sending}>
                  <InputLabel>Role</InputLabel>
                  <Select value={targetRole} label="Role" onChange={(e) => setTargetRole(e.target.value)}>
                    <MenuItem value="user">User</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="super_admin">Super Admin</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <Button variant="contained" startIcon={sending ? <Refresh /> : <Campaign />} onClick={send} disabled={sending || !message.trim()}
                sx={{ bgcolor: '#2D6E6A', textTransform: 'none', '&:hover': { bgcolor: '#1F4E4A' } }}>
                {sending ? 'Sending…' : 'Send Broadcast'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Active Broadcasts ({active.length})</Typography>
          {active.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No active broadcasts.</Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Message</TableCell><TableCell>Severity</TableCell><TableCell>Category</TableCell><TableCell>Recipient</TableCell><TableCell>Sent</TableCell><TableCell align="right">Action</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {active.map((b) => (
                    <TableRow key={b.id} hover>
                      <TableCell sx={{ maxWidth: 320 }}><Typography variant="body2">{b.message}</Typography></TableCell>
                      <TableCell><Chip size="small" label={b.severity} color={sevColor(b.severity) as any} variant="outlined" /></TableCell>
                      <TableCell><Chip size="small" label={catLabel(b.category)} variant="outlined" /></TableCell>
                      <TableCell>{recipientLabel(b.recipientType, b.role)}</TableCell>
                      <TableCell>{b.sent}</TableCell>
                      <TableCell align="right"><Button size="small" color="error" variant="outlined" onClick={() => recall(b)} sx={{ textTransform: 'none' }}>Recall</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Recalled Broadcasts ({recalled.length})</Typography>
          {recalled.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No recalled broadcasts.</Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Message</TableCell><TableCell>Category</TableCell><TableCell>Recipient</TableCell><TableCell>Recalled</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {recalled.map((b) => (
                    <TableRow key={b.id} hover sx={{ opacity: 0.7 }}>
                      <TableCell sx={{ maxWidth: 320 }}><Typography variant="body2" sx={{ textDecoration: 'line-through' }}>{b.message}</Typography></TableCell>
                      <TableCell><Chip size="small" label={catLabel(b.category)} variant="outlined" /></TableCell>
                      <TableCell>{recipientLabel(b.recipientType, b.role)}</TableCell>
                      <TableCell>{b.recalledAt}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
      <BackToHome go={go} />
    </Box>
  );
}

function AdminResources({ go }: any) {
  const [rows, setRows] = useState<any[]>([
    { id: 'r0', title: 'Your AI Investment Deserves Better Guidance', date: 'Jun 2, 2026', category: 'advisory', type: 'Hosted', status: 'draft', order: 0 },
    { id: 'r1', title: 'The 72-Hour Build', date: 'Feb 10, 2026', category: 'engineering', type: 'Blog', status: 'published', order: 1 },
    { id: 'r2', title: 'PostgreSQL Migration', date: 'Feb 22, 2026', category: 'engineering', type: 'Blog', status: 'published', order: 2 },
    { id: 'r3', title: 'Why We Picked a Launch Date', date: 'Mar 5, 2026', category: 'product', type: 'Blog', status: 'published', order: 3 },
  ]);
  const publish = (id: string) => setRows((prev) => prev.map((r) => r.id === id ? { ...r, status: 'published' } : r));
  const statusChip = (s: string) => s === 'published'
    ? { label: 'Published', bg: '#E8F5F3', fg: '#2D6E6A' }
    : s === 'draft' ? { label: 'Draft', bg: '#FFF3E0', fg: '#B26A00' } : { label: 'Cancelled', bg: '#FDECEA', fg: '#C62828' };
  return (
    <Box>
      <Narration text="Resources — the blog & content CMS. Drafts are not visible on the public site until published. Publish the draft below and watch its status flip to Published — that is the exact action that makes a post go live." />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>Resources</Typography>
        <Button variant="contained" startIcon={<AddCircleOutline />} sx={{ bgcolor: '#2D6E6A', textTransform: 'none', '&:hover': { bgcolor: '#1F4E4A' } }}>New Resource</Button>
      </Box>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead><TableRow sx={{ bgcolor: '#F5F5F5' }}>
            <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="center">Order</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {rows.map((r) => {
              const sc = statusChip(r.status);
              return (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{r.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.date}</Typography>
                  </TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>{r.category}</TableCell>
                  <TableCell>{r.type}</TableCell>
                  <TableCell><Chip size="small" label={sc.label} sx={{ bgcolor: sc.bg, color: sc.fg, fontWeight: 600 }} /></TableCell>
                  <TableCell align="center">{r.order}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      <Button size="small" variant="outlined" sx={{ textTransform: 'none', color: '#2D6E6A', borderColor: '#2D6E6A', fontSize: '0.72rem' }}>Edit</Button>
                      {r.status !== 'published'
                        ? <Button size="small" variant="contained" onClick={() => publish(r.id)} sx={{ bgcolor: '#2D6E6A', textTransform: 'none', fontSize: '0.72rem', '&:hover': { bgcolor: '#1F4E4A' } }}>Publish</Button>
                        : <Button size="small" variant="outlined" sx={{ textTransform: 'none', color: '#2D6E6A', borderColor: '#2D6E6A', fontSize: '0.72rem' }}>Hold</Button>}
                      <Button size="small" variant="outlined" sx={{ textTransform: 'none', color: '#C62828', borderColor: '#C62828', fontSize: '0.72rem' }}>Delete</Button>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <BackToHome go={go} />
    </Box>
  );
}

function AdminFlags({ go }: any) {
  const [flags, setFlags] = useState<any[]>([
    { flag_key: 'master_public', description: 'Public Trips master switch', scope: 'global', enabled: true, updated: 'May 20, 2026', by: 'alex.morgan' },
    { flag_key: 'master_friends', description: 'Friends System master switch', scope: 'global', enabled: true, updated: 'May 20, 2026', by: 'alex.morgan' },
    { flag_key: 'booking_enabled', description: 'Booking Engine', scope: 'global', enabled: false, updated: 'May 22, 2026', by: 'alex.morgan' },
    { flag_key: 'ai_activity_recommendations', description: 'AI Activity Suggestions', scope: 'global', enabled: true, updated: 'May 25, 2026', by: 'alex.morgan' },
    { flag_key: 'public_trips', description: 'Public Trip Sharing', scope: 'global', enabled: true, updated: 'May 25, 2026', by: 'alex.morgan' },
    { flag_key: 'friends_trips', description: 'Friends Trip Sharing', scope: 'global', enabled: true, updated: 'May 25, 2026', by: 'alex.morgan' },
  ]);
  const [confirm, setConfirm] = useState<any>(null);
  const [log, setLog] = useState<string[]>([]);
  const [disabledFriends, setDisabledFriends] = useState(false);

  const toggle = (flag: any) => setConfirm(flag);
  const applyToggle = () => {
    if (!confirm) return;
    const f = confirm; const newVal = !f.enabled;
    setFlags((prev) => prev.map((x) => x.flag_key === f.flag_key ? { ...x, enabled: newVal, updated: 'Just now', by: 'alex.morgan' } : x));
    setLog((prev) => [(newVal ? 'Enabled' : 'Disabled') + ' ' + f.flag_key + ' \u00b7 alex.morgan@percoro.com \u00b7 just now', ...prev]);
    if (f.flag_key === 'friends_trips') setDisabledFriends(!newVal);
    setConfirm(null);
  };
  const enabledCount = flags.filter((f) => f.enabled).length;

  return (
    <Box>
      <Narration text="Feature Flags — flip product capabilities on or off instantly, no redeploy. Toggling a flag is confirmed, audit-logged, and immediately changes what users can do. Turn off Friends Trip Sharing to see exactly which user-facing surfaces lose the feature." />
      <Typography variant="h5" fontWeight={600} gutterBottom>System Management</Typography>
      <Tabs value={0} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tab label="Feature Flags" />
      </Tabs>
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Chip icon={<CheckCircle />} label={enabledCount + ' Enabled'} color="success" variant="outlined" />
        <Chip icon={<Close />} label={(flags.length - enabledCount) + ' Disabled'} color="error" variant="outlined" />
        <Chip label={flags.length + ' Total'} variant="outlined" />
      </Box>

      {disabledFriends && (
        <Box sx={{ bgcolor: '#FDECEA', border: '1px solid #F5C6C0', borderRadius: 2, p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Warning sx={{ color: '#C62828', fontSize: 20 }} />
            <Typography variant="subtitle2" fontWeight={700} color="#C62828">Friends Trip Sharing is now OFF — here is what changed for users</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>The "Share with Friends" visibility option (BFFs & Travel Partners) no longer renders in the three places that check this flag:</Typography>
          <Box component="ul" sx={{ pl: 3, m: 0, mb: 1 }}>
            <li><Typography variant="body2" color="text.secondary">Create Trip — visibility picker drops the Friends option</Typography></li>
            <li><Typography variant="body2" color="text.secondary">Trip Detail — can no longer switch a trip to Friends visibility</Typography></li>
            <li><Typography variant="body2" color="text.secondary">Settings — the default-sharing Friends option is hidden</Typography></li>
          </Box>
          <Typography variant="caption" color="text.secondary">New trips can only be Private or Public; the change takes effect immediately for all users, no redeploy.</Typography>
        </Box>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead><TableRow sx={{ bgcolor: '#F5F5F5' }}>
            <TableCell sx={{ fontWeight: 600 }}>Flag Key</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Scope</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="center">Status</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Last Updated</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Updated By</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {flags.map((flag) => (
              <TableRow key={flag.flag_key} hover sx={{ bgcolor: flag.enabled ? '#F1F8F4' : 'inherit' }}>
                <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>{flag.flag_key}</Typography></TableCell>
                <TableCell><Typography variant="body2" color="text.secondary">{flag.description}</Typography></TableCell>
                <TableCell><Chip label={flag.scope} size="small" color="primary" variant="outlined" /></TableCell>
                <TableCell align="center"><Switch checked={flag.enabled} onChange={() => toggle(flag)} color="success" /></TableCell>
                <TableCell><Typography variant="caption" color="text.secondary">{flag.updated}</Typography></TableCell>
                <TableCell><Typography variant="caption" color="text.secondary">{flag.by}</Typography></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {log.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Audit log</Typography>
          <Box sx={{ bgcolor: '#F8FAFB', border: '1px solid #e0e0e0', borderRadius: 2, p: 2 }}>
            {log.map((line, i) => (
              <Typography key={i} variant="caption" sx={{ display: 'block', fontFamily: 'monospace', color: '#444', mb: 0.5 }}>{line}</Typography>
            ))}
          </Box>
        </Box>
      )}

      <Dialog open={confirm !== null} onClose={() => setConfirm(null)}>
        <DialogTitle>{confirm && confirm.enabled ? 'Disable' : 'Enable'} Feature Flag</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to {confirm && confirm.enabled ? 'disable' : 'enable'} <strong>{confirm && confirm.flag_key}</strong>?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{confirm && confirm.description}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button onClick={applyToggle} variant="contained" color={confirm && confirm.enabled ? 'error' : 'success'}>{confirm && confirm.enabled ? 'Disable' : 'Enable'}</Button>
        </DialogActions>
      </Dialog>
      <BackToHome go={go} />
    </Box>
  );
}

// ── ADMIN FLOW CONTROLLER ──
function AdminFlow({ onExit }: any) {
  const [screen, setScreen] = useState('signin');
  const go = (s: string) => setScreen(s);
  // lifted early-access state so approve/reject persists across navigation
  const [requests, setRequests] = useState([
    { id: 'ea1', name: 'Taylor Morgan', email: 'taylor.morgan@example.com', message: 'Excited to try Percoro for our next group trip!', date: 'Jun 11, 2026', status: 'pending' },
    { id: 'ea2', name: 'Casey Rivera', email: 'casey.rivera@example.com', message: 'Requesting early access — heard great things.', date: 'Jun 11, 2026', status: 'pending' },
    { id: 'ea3', name: 'Jamie Lin', email: 'jamie.lin@example.com', message: 'Would love to plan a family trip on this.', date: 'Jun 11, 2026', status: 'approved' },
    { id: 'ea4', name: 'Avery Patel', email: 'avery.patel@example.com', message: 'Testing the features for our travel group.', date: 'Jun 11, 2026', status: 'approved' },
    { id: 'ea5', name: 'Sam Okafor', email: 'sam.okafor@example.com', message: 'Looking forward to the companions feature.', date: 'Jun 5, 2026', status: 'approved' },
    { id: 'ea6', name: 'Robin Diaz', email: 'robin.diaz@example.com', message: 'Requesting early access to try trip planning.', date: 'Jun 2, 2026', status: 'approved' },
  ]);
  const pendingCount = requests.filter((r) => r.status === 'pending').length;

  let body: any = null;
  if (screen === 'signin') body = <AdminSignIn go={go} />;
  else if (screen === 'home') body = <AdminHome go={go} accessCount={pendingCount} />;
  else if (screen === 'support') body = <AdminSupport go={go} />;
  else if (screen === 'health') body = <AdminHealth go={go} />;
  else if (screen === 'access') body = <AdminAccess go={go} requests={requests} setRequests={setRequests} />;
  else if (screen === 'ai') body = <AdminAIOps go={go} />;
  else if (screen === 'notify') body = <AdminNotify go={go} />;
  else if (screen === 'resources') body = <AdminResources go={go} />;
  else if (screen === 'flags') body = <AdminFlags go={go} />;
  else body = <AdminHome go={go} accessCount={pendingCount} />;

  return <AdminShell active={screen} go={go} onExit={onExit}>{body}</AdminShell>;
}


const DemoPage: React.FC = () => {
  const { isEnabled } = useFeatureFlags();
  const demoEnabled = isEnabled('demo_mode');
  const [mode, setMode] = useState<'entry' | 'user' | 'admin'>('entry');
  const [step, setStep] = useState(0);
  const steps = ADMIN_STEPS;
  const isLastStep = step >= steps.length - 1;
  const StepComponent = mode === 'admin' ? steps[step]?.component : null;
  const handleNext = () => { if (step >= steps.length - 1) { setMode('entry'); setStep(0); } else { setStep(s => s + 1); } };
  const handleBack = () => { if (step === 0) { setMode('entry'); setStep(0); } else { setStep(s => s - 1); } };
  const handleReset = () => { setMode('entry'); setStep(0); };

  if (!demoEnabled) {
    return (<Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}><Typography color="text.secondary">The interactive demo is not currently available.</Typography></Box>);
  }

  // USER MODE -> new flow
  if (mode === 'user') {
    return <UserFlow onExit={handleReset} />;
  }

  // ADMIN MODE -> new flow
  if (mode === 'admin') {
    return <AdminFlow onExit={handleReset} />;
  }

  // ENTRY
  if (mode === 'entry') {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFB', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Box sx={{ maxWidth: 700, width: '100%', textAlign: 'center' }}>
          <Typography component="a" href="/" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '1.5rem', color: '#2D6E6A', mb: 0.5, display: 'inline-block', textDecoration: 'none', cursor: 'pointer' }}>PERCORO</Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif', mb: 1 }}>Interactive Demo</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 450, mx: 'auto' }}>Experience the full platform — choose your perspective.</Typography>
          <Grid container spacing={3} justifyContent="center" alignItems="stretch">
            <Grid item xs={12} sm={4}>
              <Card onClick={() => { setMode('user'); setStep(0); }} sx={{ height: '100%', borderRadius: 4, p: 3, cursor: 'pointer', transition: 'all 0.2s', border: '2px solid transparent', '&:hover': { borderColor: '#2D6E6A', transform: 'translateY(-4px)', boxShadow: '0 8px 30px rgba(0,0,0,0.12)' } }}>
                <Box sx={{ bgcolor: '#E8F0EE', borderRadius: '50%', width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}><TravelExplore sx={{ fontSize: 32, color: '#2D6E6A' }} /></Box>
                <Typography variant="h6" fontWeight={700}>User Demo</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>See the traveler experience — trips, photos, community, companions.</Typography>
                <Chip label="Interactive" size="small" sx={{ mt: 2, bgcolor: '#E8F0EE', color: '#2D6E6A' }} />
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card onClick={() => { window.location.href = '/demo/trust-safety'; }} sx={{ height: '100%', borderRadius: 4, p: 3, cursor: 'pointer', transition: 'all 0.2s', border: '2px solid transparent', '&:hover': { borderColor: '#2D6E6A', transform: 'translateY(-4px)', boxShadow: '0 8px 30px rgba(0,0,0,0.12)' } }}>
                <Box sx={{ bgcolor: '#E8F0EE', borderRadius: '50%', width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}><Shield sx={{ fontSize: 32, color: '#2D6E6A' }} /></Box>
                <Typography variant="h6" fontWeight={700}>Trust &amp; Safety</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Follow an abuse report end to end — from a user's tap to containment, review, and audit.</Typography>
                <Chip label="5 screens" size="small" sx={{ mt: 2, bgcolor: '#E8F0EE', color: '#2D6E6A' }} />
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card onClick={() => { setMode('admin'); setStep(0); }} sx={{ height: '100%', borderRadius: 4, p: 3, cursor: 'pointer', transition: 'all 0.2s', border: '2px solid transparent', '&:hover': { borderColor: '#2D6E6A', transform: 'translateY(-4px)', boxShadow: '0 8px 30px rgba(0,0,0,0.12)' } }}>
                <Box sx={{ bgcolor: '#E8EEF0', borderRadius: '50%', width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}><AdminPanelSettings sx={{ fontSize: 32, color: '#3949ab' }} /></Box>
                <Typography variant="h6" fontWeight={700}>Admin Demo</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>See the admin dashboard — feature flags, users, plans, notifications.</Typography>
                <Chip label="9 screens" size="small" sx={{ mt: 2, bgcolor: '#E8EEF0', color: '#3949ab' }} />
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  }

  // ADMIN OUTRO
  if (step >= steps.length) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFB', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Typography component="a" href="/" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '1.5rem', color: '#2D6E6A', textDecoration: 'none', cursor: 'pointer', mb: 1 }}>PERCORO</Typography>
        <Outro />
        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button onClick={handleReset} sx={{ color: '#2D6E6A', textTransform: 'none' }}>← Back to demo menu</Button>
          <Button href="/" sx={{ color: '#2D6E6A', textTransform: 'none' }}>← Back to Percoro</Button>
        </Box>
      </Box>
    );
  }

  // ADMIN STEPS (unchanged stepper)
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFB' }}>
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0', px: 2, py: 1.5, position: 'sticky', top: 0, zIndex: 10 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: 900, mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={handleBack}><ArrowBack fontSize="small" /></IconButton>
            <Typography component="a" href="/" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, color: '#2D6E6A', fontSize: '1rem', textDecoration: 'none', cursor: 'pointer' }}>PERCORO</Typography>
            <Chip label="Admin Demo" size="small" sx={{ bgcolor: '#E8EEF0', color: '#3949ab', fontWeight: 600 }} />
          </Box>
          <Button size="small" onClick={handleReset} sx={{ textTransform: 'none', color: 'text.secondary' }}>Exit Demo</Button>
        </Box>
      </Box>
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #f0f0f0', px: 2, py: 1 }}>
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
          <Stepper activeStep={step} alternativeLabel sx={{ '& .MuiStepLabel-label': { fontSize: '0.7rem' } }}>
            {steps.map((s, i) => (<Step key={i} completed={i < step}><StepLabel sx={{ cursor: 'pointer' }} onClick={() => setStep(i)}>{s.label}</StepLabel></Step>))}
          </Stepper>
        </Box>
      </Box>
      <Box sx={{ maxWidth: 700, mx: 'auto', p: 3, pb: 10 }}>{StepComponent && <StepComponent />}</Box>
      <Box sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, bgcolor: 'white', borderTop: '1px solid #e0e0e0', px: 2, py: 1.5, zIndex: 10 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', maxWidth: 700, mx: 'auto' }}>
          <Button onClick={handleBack} startIcon={<ArrowBack />} sx={{ textTransform: 'none', color: 'text.secondary' }}>{step === 0 ? 'Menu' : 'Back'}</Button>
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>{step + 1} / {steps.length}</Typography>
          <Button onClick={handleNext} endIcon={<ArrowForward />} variant="contained" sx={{ bgcolor: '#2D6E6A', textTransform: 'none', borderRadius: '20px', fontWeight: 600 }}>{isLastStep ? 'Finish' : 'Next'}</Button>
        </Box>
      </Box>
    </Box>
  );
};

export default DemoPage;
